import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, AlertTriangle, Info, LifeBuoy, Download, Menu, X, Search } from 'lucide-react';
import { MarketingNav } from '@/components/marketing/marketing-nav';
import { MarketingFooter } from '@/components/marketing/marketing-footer';
import { cn } from '@/lib/utils';
import { setDocumentMeta } from '@/lib/seo';
import { HighlightedCode, CodeBlockTabs, type CodeSnippet } from '@/components/docs/code-block-tabs';
import { DocsSearch } from '@/components/docs/cmdk-search';
import { ChangelogBanner } from '@/components/docs/changelog-banner';

// ─── Helpers ──────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className={cn(
        'absolute top-3 right-3 px-2 py-1 rounded-md text-xs font-semibold transition-all',
        copied
          ? 'text-green-400'
          : 'text-zinc-400 hover:text-white bg-white/10 hover:bg-white/20',
      )}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </button>
  );
}

function CodeBlock({ children, copyText, language }: { children: string; copyText?: string; language?: string }) {
  return (
    <HighlightedCode code={children} copyText={copyText} language={language || 'bash'} />
  );
}

function ParamTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: (string | { mono?: boolean; text: string; req?: boolean })[][];
}) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white mb-6">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-zinc-200 ">
            {headers.map((h) => (
              <th
                key={h}
                className="text-left px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-zinc-500"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 ">
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => {
                if (typeof cell === 'string') {
                  return (
                    <td key={j} className="px-3 py-2.5 text-zinc-600 align-top">
                      {cell}
                    </td>
                  );
                }
                return (
                  <td key={j} className="px-3 py-2.5 align-top">
                    {cell.mono && (
                      <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">
                        {cell.text}
                      </code>
                    )}
                    {cell.req === true && (
                      <span className="text-xs font-semibold bg-red-100 text-red-600 px-1.5 py-0.5 rounded">
                        required
                      </span>
                    )}
                    {cell.req === false && (
                      <span className="text-xs font-semibold bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded">
                        optional
                      </span>
                    )}
                    {!cell.mono && cell.req === undefined && (
                      <span className="text-zinc-600 ">{cell.text}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MethodBadge({ method }: { method: 'GET' | 'POST' | 'PATCH' | 'DELETE' }) {
  const styles = {
    GET: 'bg-green-100 text-green-700 ',
    POST: 'bg-blue-100 text-blue-700 ',
    PATCH: 'bg-amber-100 text-amber-700 ',
    DELETE: 'bg-red-100 text-red-700 ',
  };
  return (
    <span
      className={cn(
        'font-mono text-xs font-bold px-2.5 py-1 rounded-lg',
        styles[method],
      )}
    >
      {method}
    </span>
  );
}

function EndpointCard({
  id,
  method,
  path,
  description,
  children,
}: {
  id: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="doc-section mb-8 scroll-mt-24">
      <div className="bg-white border border-zinc-200 rounded-[22px] overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-zinc-100 bg-zinc-50/95">
          <div className="flex items-center gap-3">
            <MethodBadge method={method} />
            <h2 className="font-mono text-base font-semibold text-zinc-900 tracking-tight break-all">
              {path}
            </h2>
          </div>
          <p className="text-sm text-zinc-500 mt-1.5">{description}</p>
        </div>
        <div className="p-5 sm:p-6">
          {children}
        </div>
      </div>
    </section>
  );
}

function SectionTitle({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <h3 id={id} className="text-sm font-semibold text-zinc-900 mb-2 mt-6 scroll-mt-24">
      {children}
    </h3>
  );
}

function DocCard({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="doc-section mb-8 scroll-mt-24">
      <div className="bg-white border border-zinc-200 rounded-[22px] p-5 sm:p-7">
        <h2 className="font-display text-2xl sm:text-[28px] leading-tight text-zinc-900 mb-4">
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}

// ─── SEO: Per-section meta ────────────────────────────────

const sectionMeta: Record<string, { title: string; desc: string }> = {
  overview: { title: 'Kora REST API Reference — Tickets, Webhooks, Auth', desc: 'Kora Public API v1 reference: tickets, comments, attachments and members over REST, with API-key auth, webhooks and rate limits. Every workspace.' },
  'quick-start': { title: 'Quick Start — Kora Docs', desc: 'Make your first Kora API request in 30 seconds with cURL, JavaScript, Python, or Go examples.' },
  authentication: { title: 'Authentication — Kora Docs', desc: 'Authenticate Kora API requests with X-Workspace-ID and X-Secret-Key headers.' },
  'base-url': { title: 'Base URL — Kora Docs', desc: 'Base URL for all Kora Public API endpoints: api.usekora.app/public/v1' },
  'rate-limits': { title: 'Rate Limits — Kora Docs', desc: 'Kora API rate limits: 50,000 req/day with a funded credit wallet, 5,000 req/day when empty.' },
  errors: { title: 'Error Codes — Kora Docs', desc: 'Kora API error codes and error response format reference.' },
  'list-tickets': { title: 'GET /tickets — List Tickets API', desc: 'Retrieve paginated tickets within a date range. Filter by reporter. Max 92 days, 100 per page.' },
  'create-ticket': { title: 'POST /tickets — Create Ticket API', desc: 'Create a new ticket via the Kora Public API with title, description, category, priority.' },
  'get-ticket': { title: 'GET /tickets/:id — Get Ticket API', desc: 'Retrieve a single ticket by its ticket number via the Kora Public API.' },
  'update-ticket': { title: 'PATCH /tickets/:id — Update Ticket', desc: 'Update ticket status, priority, or fields via the Kora Public API.' },
  'get-comments': { title: 'GET /tickets/:id/comments — Kora', desc: 'Fetch all comments and attachments for a ticket via the Kora Public API.' },
  'add-comment': { title: 'POST /tickets/:id/comments — Kora', desc: 'Add a comment to an existing ticket via the Kora Public API.' },
  'dashboard-metrics': { title: 'GET /dashboard/metrics — Kora', desc: 'Monthly aggregated metrics and chart data for custom dashboards via Kora API.' },
  'ticket-object': { title: 'Ticket Object — Kora API Reference', desc: 'Ticket object schema: fields, types, and descriptions for the Kora Public API.' },
  'comment-object': { title: 'Comment Object — Kora API Reference', desc: 'Comment object schema: fields, types, and descriptions for the Kora Public API.' },
  'response-headers': { title: 'Response Headers — Kora API Ref', desc: 'Rate limit and CORS response headers returned by all Kora Public API endpoints.' },
  'embedded-overview': { title: 'Embedded Sessions — Kora Docs', desc: 'Embed Kora ticket forms into external websites with session-based embedded sessions.' },
  'create-session': { title: 'Create Embedded Session — Kora', desc: 'Create an embedded session to collect tickets from external websites via the Kora API.' },
  'embed-examples': { title: 'Embedding Examples — Kora Docs', desc: 'Code examples for embedding Kora ticket forms into websites using session tokens.' },
  'session-behavior': { title: 'Session Behavior — Kora Docs', desc: 'How embedded sessions behave: expiration, rate limits, and ticket submission rules.' },
  'session-security': { title: 'Embedded Session Security — Kora', desc: 'Security model for embedded sessions: CORS, domain restrictions, and token handling.' },
  'webhooks-overview': { title: 'Webhooks — Kora Docs', desc: 'Receive real-time HTTP notifications when ticket events happen in your Kora workspace.' },
  'webhook-events': { title: 'Webhook Events — Kora Docs', desc: 'Catalog of all webhook event types and their payload structures.' },
  'webhook-payload': { title: 'Webhook Payload — Kora Docs', desc: 'Webhook envelope structure, HTTP headers, and signature verification.' },
  'manage-webhooks': { title: 'Managing Webhooks — Kora Docs', desc: 'Create, test, and delete webhook endpoints from Settings or via the API.' },
  'list-members': { title: 'GET /members — List Members API', desc: 'List all active workspace members via the Kora Public API.' },
  'create-webhook-api': { title: 'POST /webhooks — Create Webhook API', desc: 'Create a new webhook endpoint programmatically via the Kora Public API.' },
  'credits-overview': { title: 'Credits & Billing — Kora Docs', desc: 'How Kora pay-as-you-go credits work: $1 = 10 credits, 1 credit per ticket, metering, top-ups, and the 402 CREDITS_EXHAUSTED response.' },
  'categories-overview': { title: 'Categories API — Kora Docs', desc: 'Manage workspace ticket categories: list, create, rename (cascades), delete guards, and the AI category-creation toggle.' },
  'automations-overview': { title: 'Automation API — Kora Docs', desc: 'Create, test, and manage if-this-then-that automation rules for tickets: triggers, conditions, actions, dry-run, and run history.' },
  'helpcenter-overview': { title: 'Helpdesk API — Kora Docs', desc: 'Blog-style knowledge base: article CRUD, publish (1 credit, once), claimable help-center links, settings and branding, analytics, subscribers, and public article endpoints.' },
};

// ─── Sidebar ──────────────────────────────────────────────

const sidebarSections = [
  {
    title: 'Getting Started',
    links: [
      { id: 'overview', label: 'Overview', method: null },
      { id: 'quick-start', label: 'Quick Start', method: null },
      { id: 'authentication', label: 'Authentication', method: null },
      { id: 'base-url', label: 'Base URL', method: null },
      { id: 'rate-limits', label: 'Rate Limits', method: null },
      { id: 'errors', label: 'Errors', method: null },
    ],
  },
  {
    title: 'Workspace',
    links: [
      { id: 'project-summary', label: 'Project Summary', method: 'GET' as const },
    ],
  },
  {
    title: 'Tickets',
    links: [
      { id: 'list-tickets', label: 'List Tickets', method: 'GET' as const },
      { id: 'create-ticket', label: 'Create Ticket', method: 'POST' as const },
      { id: 'get-ticket', label: 'Get Ticket', method: 'GET' as const },
      { id: 'update-ticket', label: 'Update Ticket', method: 'PATCH' as const },
      { id: 'claim-ticket', label: 'Claim Ticket', method: 'POST' as const },
      { id: 'merge-ticket', label: 'Merge Ticket', method: 'POST' as const },
      { id: 'bulk-update', label: 'Bulk Update', method: 'POST' as const },
    ],
  },
  {
    title: 'Comments',
    links: [
      { id: 'get-comments', label: 'Get Comments', method: 'GET' as const },
      { id: 'add-comment', label: 'Add Comment', method: 'POST' as const },
      { id: 'comments-since', label: 'Comments Since', method: 'GET' as const },
    ],
  },
  {
    title: 'Attachments',
    links: [
      { id: 'list-attachments', label: 'List Attachments', method: 'GET' as const },
      { id: 'upload-attachment', label: 'Upload Attachment', method: 'POST' as const },
    ],
  },
  {
    title: 'Analytics',
    links: [
      { id: 'dashboard-metrics', label: 'Dashboard Metrics', method: 'GET' as const },
    ],
  },
  {
    title: 'Members',
    links: [
      { id: 'list-members', label: 'List Members', method: 'GET' as const },
      { id: 'invite-member', label: 'Invite Member', method: 'POST' as const },
      { id: 'update-member', label: 'Update Member Role', method: 'PATCH' as const },
    ],
  },
  {
    title: 'Webhooks',
    links: [
      { id: 'webhooks-overview', label: 'Overview', method: null },
      { id: 'webhook-events', label: 'Event Catalog', method: null },
      { id: 'webhook-payload', label: 'Payload & Headers', method: null },
      { id: 'list-webhooks', label: 'List Webhooks', method: 'GET' as const },
      { id: 'create-webhook-api', label: 'Create Webhook', method: 'POST' as const },
      { id: 'update-webhook', label: 'Update Webhook', method: 'PATCH' as const },
      { id: 'delete-webhook', label: 'Delete Webhook', method: 'DELETE' as const },
      { id: 'test-webhook', label: 'Test Webhook', method: 'POST' as const },
      { id: 'webhook-deliveries', label: 'Webhook Deliveries', method: 'GET' as const },
    ],
  },
  {
    title: 'Embedded Sessions',
    links: [
      { id: 'embedded-overview', label: 'Overview', method: null },
      { id: 'create-session', label: 'Create Session', method: 'POST' as const },
      { id: 'embed-examples', label: 'Embedding Examples', method: null },
      { id: 'session-behavior', label: 'Session Behavior', method: null },
      { id: 'session-security', label: 'Security', method: null },
    ],
  },
  {
    title: 'Reference',
    links: [
      { id: 'ticket-object', label: 'Ticket Object', method: null },
      { id: 'comment-object', label: 'Comment Object', method: null },
      { id: 'response-headers', label: 'Response Headers', method: null },
    ],
  },
  {
    title: 'Credits & Billing',
    links: [
      { id: 'credits-overview', label: 'How credits work', method: null },
    ],
  },
  {
    title: 'Categories',
    links: [
      { id: 'categories-overview', label: 'Categories API', method: null },
    ],
  },
  {
    title: 'Automation',
    links: [
      { id: 'automations-overview', label: 'Automation Rules', method: null },
      { id: 'helpcenter-overview', label: 'Helpdesk', method: null },
    ],
  },
];

const tocSections = [
  {
    title: 'Getting Started',
    links: [
      { id: 'overview', label: 'Overview' },
      { id: 'quick-start', label: 'Quick Start' },
      { id: 'base-url', label: 'Base URL' },
      { id: 'authentication', label: 'Authentication' },
      { id: 'rate-limits', label: 'Rate Limits' },
      { id: 'errors', label: 'Errors' },
    ],
  },
  {
    title: 'Workspace',
    links: [
      { id: 'project-summary', label: 'Project Summary' },
    ],
  },
  {
    title: 'Tickets',
    links: [
      { id: 'list-tickets', label: 'List Tickets' },
      { id: 'create-ticket', label: 'Create Ticket' },
      { id: 'get-ticket', label: 'Get Ticket' },
      { id: 'update-ticket', label: 'Update Ticket' },
      { id: 'claim-ticket', label: 'Claim Ticket' },
      { id: 'merge-ticket', label: 'Merge Ticket' },
      { id: 'bulk-update', label: 'Bulk Update' },
    ],
  },
  {
    title: 'Comments',
    links: [
      { id: 'get-comments', label: 'Get Comments' },
      { id: 'add-comment', label: 'Add Comment' },
      { id: 'comments-since', label: 'Comments Since' },
    ],
  },
  {
    title: 'Attachments',
    links: [
      { id: 'list-attachments', label: 'List Attachments' },
      { id: 'upload-attachment', label: 'Upload Attachment' },
    ],
  },
  {
    title: 'Analytics',
    links: [
      { id: 'dashboard-metrics', label: 'Dashboard Metrics' },
    ],
  },
  {
    title: 'Members',
    links: [
      { id: 'list-members', label: 'List Members' },
      { id: 'invite-member', label: 'Invite Member' },
      { id: 'update-member', label: 'Update Member Role' },
    ],
  },
  {
    title: 'Embedded',
    links: [
      { id: 'embedded-overview', label: 'Overview' },
      { id: 'create-session', label: 'Create Session' },
      { id: 'embed-examples', label: 'Embedding Examples' },
      { id: 'session-behavior', label: 'Session Behavior' },
      { id: 'session-security', label: 'Security' },
    ],
  },
  {
    title: 'Webhooks',
    links: [
      { id: 'webhooks-overview', label: 'Overview' },
      { id: 'webhook-events', label: 'Event Catalog' },
      { id: 'webhook-payload', label: 'Payload & Headers' },
      { id: 'list-webhooks', label: 'List Webhooks' },
      { id: 'create-webhook-api', label: 'Create Webhook' },
      { id: 'update-webhook', label: 'Update Webhook' },
      { id: 'delete-webhook', label: 'Delete Webhook' },
      { id: 'test-webhook', label: 'Test Webhook' },
      { id: 'webhook-deliveries', label: 'Deliveries' },
    ],
  },
  {
    title: 'Reference',
    links: [
      { id: 'ticket-object', label: 'Ticket Object' },
      { id: 'comment-object', label: 'Comment Object' },
      { id: 'response-headers', label: 'Response Headers' },
    ],
  },
  {
    title: 'Credits & Billing',
    links: [
      { id: 'credits-overview', label: 'How credits work', method: null },
    ],
  },
  {
    title: 'Categories',
    links: [
      { id: 'categories-overview', label: 'Categories API', method: null },
    ],
  },
  {
    title: 'Automation',
    links: [
      { id: 'automations-overview', label: 'Automation Rules', method: null },
      { id: 'helpcenter-overview', label: 'Helpdesk', method: null },
    ],
  },
];

// Sub-headings within each endpoint/section card, used for the right TOC tree.
const endpointSubsections: Record<string, { id: string; label: string }[]> = {
  'list-tickets': [
    { id: 'list-tickets-query-parameters', label: 'Query Parameters' },
    { id: 'list-tickets-request', label: 'Request' },
    { id: 'list-tickets-response', label: 'Response' },
  ],
  'create-ticket': [
    { id: 'create-ticket-request-body', label: 'Request Body' },
    { id: 'create-ticket-request', label: 'Request' },
    { id: 'create-ticket-response', label: 'Response' },
  ],
  'get-ticket': [
    { id: 'get-ticket-path-parameters', label: 'Path Parameters' },
    { id: 'get-ticket-request', label: 'Request' },
    { id: 'get-ticket-response', label: 'Response' },
  ],
  'update-ticket': [
    { id: 'update-ticket-status-transition-map', label: 'Status Transition Map' },
    { id: 'update-ticket-request-body', label: 'Request Body' },
    { id: 'update-ticket-request', label: 'Request' },
    { id: 'update-ticket-response', label: 'Response' },
  ],
  'get-comments': [
    { id: 'get-comments-path-parameters', label: 'Path Parameters' },
    { id: 'get-comments-request', label: 'Request' },
    { id: 'get-comments-response', label: 'Response' },
  ],
  'add-comment': [
    { id: 'add-comment-request-body', label: 'Request Body' },
    { id: 'add-comment-request', label: 'Request' },
    { id: 'add-comment-response', label: 'Response' },
  ],
  'dashboard-metrics': [
    { id: 'dashboard-metrics-query-parameters', label: 'Query Parameters' },
    { id: 'dashboard-metrics-request', label: 'Request' },
    { id: 'dashboard-metrics-response', label: 'Response' },
  ],
  'list-members': [
    { id: 'list-members-request', label: 'Request' },
    { id: 'list-members-response', label: 'Response' },
  ],
  'create-webhook-api': [
    { id: 'create-webhook-api-request-body', label: 'Request Body' },
    { id: 'create-webhook-api-request', label: 'Request' },
    { id: 'create-webhook-api-response', label: 'Response' },
  ],
  'create-session': [
    { id: 'create-session-request-body', label: 'Request Body' },
    { id: 'create-session-request', label: 'Request' },
    { id: 'create-session-response', label: 'Response' },
  ],
  'embed-examples': [
    { id: 'embed-examples-html', label: 'HTML' },
    { id: 'embed-examples-react', label: 'React' },
    { id: 'embed-examples-vue', label: 'Vue' },
    { id: 'embed-examples-python-djangoflask', label: 'Python (Django/Flask)' },
    { id: 'embed-examples-nextjs-serverside-session-crea', label: 'Next.js (Server-side)' },
  ],
  'webhooks-overview': [
    { id: 'webhooks-overview-how-it-works', label: 'How it works' },
  ],
  'webhook-events': [
    { id: 'webhook-events-example-ticketcreated-payload', label: 'ticket.created' },
    { id: 'webhook-events-example-ticketresolved-payload', label: 'ticket.resolved' },
  ],
  'webhook-payload': [
    { id: 'webhook-payload-envelope', label: 'Envelope' },
    { id: 'webhook-payload-http-headers', label: 'HTTP Headers' },
    { id: 'webhook-payload-signature-verification', label: 'Signature Verification' },
  ],
  'manage-webhooks': [
    { id: 'manage-webhooks-from-the-dashboard', label: 'From the Dashboard' },
  ],
};

// ─── Downloadable Markdown ────────────────────────────────

function generateDocsMarkdown(): string {
  return `# Kora Public API Reference v1

> Integrate your Kora workspace data into dashboards, automations, and internal tools with the Kora Public API.

**Format:** JSON  
**Version:** v1  
**Auth:** API Key (headers)

---

## Overview

The Kora Public API is a workspace-scoped REST API that gives programmatic access to tickets, comments, and analytics. All requests are authenticated via API key headers and return JSON.

---

## Base URL

\`\`\`
https://api.usekora.app/public/v1
\`\`\`

All endpoints are prefixed with \`/public/v1\`. The Kora Dashboard API (used internally by the app) is separate and not accessible via Public API keys.

---

## Authentication

Every request must include two headers identifying your workspace and proving your identity.

| Header | Required | Description |
|--------|----------|-------------|
| \`X-Workspace-ID\` | required | Your workspace's unique identifier. Find it in Settings → API inside the Kora app. |
| \`X-Secret-Key\` | required | Your workspace's secret API key. Treat this like a password — never expose it in client-side code. |

\`\`\`bash
# Example request with auth headers
curl https://api.usekora.app/public/v1/tickets \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key" \\
  -G \\
  --data-urlencode "start_date=2026-06-01" \\
  --data-urlencode "end_date=2026-06-30"
\`\`\`

> **Warning:** Keep your \`X-Secret-Key\` private. If compromised, regenerate it from Settings → API in the Kora app.

---

## Rate Limits

Rate limits are applied per workspace, across all Public API endpoints. Dashboard app usage does not count toward the limit.

| Plan | Daily limit | Grace buffer |
|------|------------|--------------|
| Free | 5,000 requests / day | +5% |
| Growth | 50,000 requests / day | +5% |
| Custom | Custom (contact sales) | +5% |

Every response includes these headers so you can track your usage:

| Header | Description |
|--------|-------------|
| \`X-RateLimit-Limit\` | Your plan's daily request cap |
| \`X-RateLimit-Remaining\` | Requests remaining in the current window |
| \`X-RateLimit-Reset\` | Unix timestamp when the window resets |

When the limit is exceeded, the API returns \`429 Too Many Requests\` with a \`retryAfter\` field indicating when to retry.

---

## Errors

All errors return a JSON body with \`success: false\`, a human-readable \`error\` string, and the HTTP \`code\`.

| Status | Meaning |
|--------|---------|
| \`400\` | Bad request — missing or invalid parameters |
| \`401\` | Unauthorised — invalid or missing API key headers |
| \`404\` | Not found — the requested resource doesn't exist in your workspace |
| \`429\` | Rate limit exceeded — slow down and retry after the reset window |
| \`500\` | Internal server error — contact founder@usekora.app |

\`\`\`json
{
  "success": false,
  "error": "start_date and end_date are required",
  "code": 400
}
\`\`\`

---

## Endpoints

### GET /tickets — List Tickets

List and filter tickets within a date range.

Returns a list of tickets created within the specified date range. Maximum range is **92 days**. Results are ordered newest first, with a hard cap of 10,000 tickets per response.

**Query Parameters**

| Parameter | Type | Required | Description |
|-----------|------|----------|-------------|
| \`start_date\` | string | required | Start of date range. Format: YYYY-MM-DD |
| \`end_date\` | string | required | End of date range. Format: YYYY-MM-DD. Max 92 days from start_date. |
| \`reporter_email\` | string | optional | Filter by the reporter's email address. |

\`\`\`bash
curl https://api.usekora.app/public/v1/tickets \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key" \\
  -G \\
  --data-urlencode "start_date=2026-06-01" \\
  --data-urlencode "end_date=2026-06-30"
\`\`\`

\`\`\`json
{
  "success": true,
  "workspace_id": "ws_your_workspace_id",
  "request_range": {
    "start_date": "2026-06-01",
    "end_date": "2026-06-30",
    "duration_days": 29
  },
  "tickets": [
    {
      "ticket_number": 42,
      "title": "Login page throws 500 on Safari",
      "description": "Reproducible on Safari 17.x — ...",
      "category": "Bug",
      "priority": "High",
      "status": "Open",
      "reporter": "John Doe",
      "assignee": "Jane Smith",
      "created_at": "2026-06-15T09:41:22Z",
      "resolved_at": null
    }
  ],
  "total_count": 1,
  "returned_count": 1
}
\`\`\`

### POST /tickets — Create Ticket

Create a new ticket in your workspace.

Creates a new ticket with status \`Open\`. The \`reporter_email\` must be an active workspace member.

**Request Body**

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| \`title\` | string | required | Short title for the ticket. |
| \`description\` | string | required | Full description of the issue. |
| \`category\` | string | required | Category label, e.g. Bug, Feature Request, Incident. Free-form string. |
| \`priority\` | string | required | One of: Low, Medium, High, Critical |
| \`reporter_email\` | string | required | Email of the reporter. Must be an active workspace member. |

\`\`\`bash
curl -X POST https://api.usekora.app/public/v1/tickets \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Checkout button unresponsive on mobile",
    "description": "Tapping the checkout button on iOS Safari does nothing.",
    "category": "Bug",
    "priority": "High",
    "reporter_email": "john@example.com"
  }'
\`\`\`

\`\`\`json
{
  "success": true,
  "workspace_id": "ws_your_workspace_id",
  "ticket": {
    "ticket_number": 43,
    "title": "Checkout button unresponsive on mobile",
    "description": "Tapping the checkout button on iOS Safari does nothing.",
    "category": "Bug",
    "priority": "High",
    "status": "Open",
    "reporter": "John Doe",
    "assignee": null,
    "created_at": "2026-06-19T18:30:00Z"
  }
}
\`\`\`

### GET /tickets/:ticket_id — Get Ticket

Get a single ticket with full details and attachments.

Returns the full ticket details including reporter, assignee, and attachments. The \`:ticket_id\` is the ticket's numeric \`ticket_number\`.

**Path Parameters**

| Parameter | Type | Required | Description |
|-----------|------|----------|-------------|
| \`ticket_id\` | integer | required | The ticket's numeric identifier (ticket_number), e.g. 42. |

\`\`\`bash
curl https://api.usekora.app/public/v1/tickets/42 \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key"
\`\`\`

\`\`\`json
{
  "success": true,
  "workspace_id": "ws_your_workspace_id",
  "ticket": {
    "ticket_number": 42,
    "title": "Login page throws 500 on Safari",
    "description": "Reproducible on Safari 17.x — ...",
    "category": "Bug",
    "priority": "High",
    "status": "Open",
    "reporter": "John Doe",
    "assignee": "Jane Smith",
    "created_at": "2026-06-15T09:41:22Z",
    "resolved_at": null,
    "updated_at": "2026-06-15T09:41:22Z",
    "attachments": []
  }
}
\`\`\`

### PATCH /tickets/:ticket_id — Update Ticket

Partially update a ticket's status, priority, or content.

All fields are optional — only the fields you send are changed. Uses the ticket's \`ticket_number\` as the path parameter.

> **Info:** Status transitions are validated. You can only move to a status that is reachable from the current one. Invalid transitions return \`400\` with the list of allowed next statuses.

**Status Transition Map**

| Current status | Allowed transitions |
|----------------|---------------------|
| \`Open\` | Assigned, In Progress, Resolved, Closed |
| \`Assigned\` | In Progress, Waiting for User, Waiting for Team, Resolved, Closed |
| \`In Progress\` | Waiting for User, Waiting for Team, Resolved, Closed |
| \`Waiting for User\` | In Progress, Resolved, Closed |
| \`Waiting for Team\` | In Progress, Resolved, Closed |
| \`Resolved\` | Closed, Reopened |
| \`Closed\` | Reopened |
| \`Reopened\` | Assigned, In Progress, Resolved, Closed |

**Request Body**

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| \`status\` | string | optional | New status. Must be a valid transition from current status. |
| \`priority\` | string | optional | One of: Low, Medium, High, Critical |
| \`title\` | string | optional | Updated ticket title. |
| \`description\` | string | optional | Updated ticket description. |
| \`category\` | string | optional | Updated category label. |
| \`assignee_email\` | string | optional | Email of an active workspace member to assign. |

\`\`\`bash
curl -X PATCH https://api.usekora.app/public/v1/tickets/43 \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "status": "In Progress",
    "priority": "Critical",
    "assignee_email": "jane@example.com"
  }'
\`\`\`

\`\`\`json
{
  "success": true,
  "workspace_id": "ws_your_workspace_id",
  "ticket": {
    "ticket_number": 43,
    "title": "Checkout button unresponsive on mobile",
    "description": "Tapping the checkout button on iOS Safari does nothing.",
    "category": "Bug",
    "priority": "Critical",
    "status": "In Progress",
    "reporter": "John Doe",
    "assignee": "Jane Smith",
    "created_at": "2026-06-19T18:30:00Z",
    "resolved_at": null,
    "updated_at": "2026-06-19T19:00:00Z"
  }
}
\`\`\`

### GET /tickets/:ticket_id/comments — Get Comments

Fetch comments and attachments for a ticket.

Returns all comments (and their attachments) for a specific ticket, ordered oldest-first.

**Path Parameters**

| Parameter | Type | Required | Description |
|-----------|------|----------|-------------|
| \`ticket_id\` | integer | required | The ticket's numeric identifier (ticket_number), e.g. 42. |

\`\`\`bash
curl https://api.usekora.app/public/v1/tickets/42/comments \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key"
\`\`\`

\`\`\`json
{
  "success": true,
  "workspace_id": "ws_your_workspace_id",
  "ticket_id": 42,
  "comments": [
    {
      "id": 1,
      "content": "Reproduced locally. Digging into the auth middleware.",
      "parent_id": null,
      "created_at": "2026-06-15T10:05:00Z",
      "user": "Jane Smith",
      "attachments": [
        {
          "id": 1,
          "file_name": "screenshot.png",
          "file_type": "image/png",
          "file_size": 204800,
          "storage_url": "https://..."
        }
      ]
    }
  ],
  "total_count": 1
}
\`\`\`

### POST /tickets/:ticket_id/comments — Add Comment

Add a comment to a ticket.

Creates a new comment on the specified ticket. The \`author_email\` must be an active workspace member. Supports threaded replies via \`parent_id\`.

**Request Body**

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| \`content\` | string | required | Comment body text. |
| \`author_email\` | string | required | Email of an active workspace member who is the comment author. |
| \`parent_id\` | integer | optional | ID of the parent comment for threaded replies. null for top-level comments. |

\`\`\`bash
curl -X POST https://api.usekora.app/public/v1/tickets/42/comments \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content": "Fixed in PR #123. Awaiting review.",
    "author_email": "jane@example.com"
  }'
\`\`\`

\`\`\`json
{
  "success": true,
  "workspace_id": "ws_your_workspace_id",
  "ticket_id": 42,
  "comment": {
    "id": 2,
    "content": "Fixed in PR #123. Awaiting review.",
    "user": "Jane Smith",
    "parent_id": null,
    "attachments": [],
    "created_at": "2026-06-15T10:30:00Z"
  }
}
\`\`\`

### GET /dashboard/metrics — Dashboard Metrics

Monthly analytics for custom dashboards and reports.

Returns aggregated metrics and chart data for a given calendar month. Useful for building custom dashboards, weekly reports, or Slack digests.

**Query Parameters**

| Parameter | Type | Required | Description |
|-----------|------|----------|-------------|
| \`month\` | string | required | Calendar month to query. Format: YYYY-MM, e.g. 2026-06. |

\`\`\`bash
curl "https://api.usekora.app/public/v1/dashboard/metrics?month=2026-06" \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key"
\`\`\`

\`\`\`json
{
  "success": true,
  "workspace_id": "ws_your_workspace_id",
  "month": "2026-06",
  "metrics": {
    "tickets_created": 134,
    "tickets_open": 22,
    "tickets_closed": 112,
    "tickets_reopened": 5
  },
  "charts": {
    "status_distribution": {
      "Open": 18,
      "In Progress": 4,
      "Resolved": 107,
      "Closed": 5
    },
    "tickets_by_user": [
      { "user": "Alice", "count": 41 },
      { "user": "Bob", "count": 29 }
    ],
    "tickets_by_category": [
      { "category": "Bug", "count": 58 },
      { "category": "Feature Request", "count": 46 }
    ],
    "daily_trend": [
      { "date": "2026-06-01", "count": 6 },
      { "date": "2026-06-02", "count": 9 }
    ]
  }
}
\`\`\`

---

### GET /members — List Members

List all active workspace members.

\`\`\`bash
curl https://api.usekora.app/public/v1/members \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key"
\`\`\`

\`\`\`json
{
  "success": true,
  "workspace_id": "ws_abc123",
  "members": [
    {
      "id": 1,
      "email": "john@acmesolutions.com",
      "display_name": "John",
      "role": "owner",
      "status": "active",
      "joined_at": "2026-05-01T10:00:00Z",
      "last_active_at": "2026-07-13T18:30:00Z"
    }
  ],
  "total_count": 1
}
\`\`\`

---

### POST /webhooks — Create Webhook

Create a new webhook endpoint programmatically.

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| \`url\` | string | required | HTTPS URL where webhook deliveries will be sent. |
| \`description\` | string | optional | Optional description for the webhook. |
| \`events\` | string[] | optional | Array of event names to subscribe to. Defaults to all events. |
| \`secret_token\` | string | optional | Optional secret token for HMAC-SHA256 signature verification. |

\`\`\`bash
curl -X POST https://api.usekora.app/public/v1/webhooks \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://your-app.com/webhooks/kora", "events": ["ticket.created"]}'
\`\`\`

---

## Embedded Sessions

Embedded Sessions allow you to embed the Kora tickets board directly into your product via iframe — no separate login required for your users. Call the API to generate a one-time session URL, embed it, and Kora handles the rest. The embedded view renders the full tickets board with role-based access, search, filters, and ticket management.

**Availability:** Every workspace  
**Auth:** Workspace ID + Secret Key  
**Token type:** Time-limited

**How it works:** 1) Call \`POST /public/v1/sessions\` with your workspace credentials. 2) API returns a one-time session URL. 3) Embed the URL in an iframe. 4) Kora auto-authenticates the user and renders the tickets board with role-based permissions.

### POST /public/v1/sessions — Create Embedded Session

Creates a session URL that auto-authenticates the specified user and renders Kora inside an iframe. The session token is valid for the configured duration and can be refreshed until expiry.

**Request Body**

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| \`user_email\` | string | required | Email of the user this session is for. Must be an active workspace member. |
| \`duration_minutes\` | integer | required | Session duration in minutes. Min: 5, Max: 1440 (24 hours). Default: 480. |
| \`theme\` | string | optional | Theme for embedded session. Default: light. Options: light, dark. |

\`\`\`bash
curl -X POST https://api.usekora.app/public/v1/sessions \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_email": "john@example.com",
    "duration_minutes": 480,
    "theme": "light"
  }'
\`\`\`

\`\`\`json
{
  "success": true,
  "session": {
    "session_id": "sess_abc123xyz",
    "session_url": "https://usekora.app/embedd/sess_abc123xyz",
    "embed_code": "<iframe src='https://usekora.app/embedd/sess_abc123xyz' width='100%' height='100%' frameborder='0' style='border:none;'></iframe>",
    "expires_at": "2026-06-16T23:39:00Z",
    "duration_minutes": 480
  }
}
\`\`\`

### Embedding Examples

**HTML**
\`\`\`html
<iframe
  src="https://usekora.app/embedd/sess_abc123xyz"
  width="100%"
  height="100vh"
  frameborder="0"
  style="border: none;">
</iframe>
\`\`\`

**React**
\`\`\`jsx
function KoraEmbed({ sessionUrl }) {
  return (
    <iframe
      src={sessionUrl}
      width="100%"
      height="100vh"
      frameBorder="0"
      style={{ border: 'none' }}
    />
  );
}
\`\`\`

**Python (Django/Flask)**
\`\`\`html
<iframe
  src="{{ kora_session_url }}"
  width="100%"
  height="800px"
  frameborder="0">
</iframe>
\`\`\`

### Session Behavior

| Property | Description |
|----------|-------------|
| \`Time-limited\` | Session stays valid for the configured duration. Refreshing the iframe re-validates successfully until expiry. First access is logged for audit (IP, user agent). |
| \`Auto-authentication\` | The specified user is automatically logged in — no separate login required. |
| \`Auto-expiry\` | Session expires after duration_minutes. On expiry, shows "Session Expired" page. |
| \`Tickets only\` | Embedded sessions render the Kora tickets board exclusively. No navigation to other sections is available within the iframe. |
| \`Theme\` | Renders in light or dark mode based on the theme parameter. |
| \`Role-based access\` | The tickets board respects the user role: operations roles see only their own reported tickets, while manager+ roles see all tickets. Ticket creation and claim actions are gated by role permissions. |

### Security

- Session tokens are cryptographically random and unique.
- First access is logged (IP, user agent) for audit. Token remains valid until expiry.
- Sessions are bound to a specific workspace and user.
- Session usage is logged with IP address and user agent for audit purposes.
- Expired and used sessions are automatically cleaned up.

> **Warning:** Always create sessions server-side. Never expose your \`X-Secret-Key\` in client-side code.

---

## Webhooks

Webhooks let you receive real-time HTTP notifications when ticket events happen in your workspace. When an event occurs, Kora sends a \`POST\` request to each registered webhook URL with a JSON payload describing the event.

Webhooks are configured from **Settings → Webhooks** in the Kora dashboard. You can register up to **10 webhook URLs** per workspace and select which events each URL should receive.

### How it works

1. A ticket event occurs (e.g. ticket created, resolved, closed).
2. Kora finds all active webhooks subscribed to that event.
3. For each matching webhook, Kora sends an HTTP POST with the event payload.
4. The delivery is logged with status and response code for debugging.

> **Info:** Your endpoint must respond with a \`2xx\` status code within 10 seconds. Failed deliveries are logged but not retried.

### Event Catalog

Kora fires the following events. By default, a webhook receives all events. You can deselect specific events when creating or editing a webhook.

| Event | Triggered When | Key Data Fields |
|-------|---------------|-----------------|
| \`ticket.created\` | A new ticket is created | ticket_number, title, priority, status, reporter |
| \`ticket.updated\` | Ticket fields are edited (title, priority, category, description) | ticket_number, changes[], updated_by |
| \`ticket.resolved\` | Ticket status changes to Resolved | ticket_number, previous_status, resolved_by, resolved_at |
| \`ticket.closed\` | Ticket status changes to Closed | ticket_number, previous_status, closed_by, closed_at |
| \`ticket.reopened\` | Ticket status changes to Reopened | ticket_number, previous_status, reopened_by, reopened_at |
| \`ticket.assigned\` | A ticket is assigned to a member | ticket_number, assignee, previous_assignee, assigned_by |
| \`ticket.claimed\` | An agent claims an unassigned ticket | ticket_number, claimed_by, previous_status, claimed_at |

### Example: ticket.created payload

\`\`\`json
{
  "event": "ticket.created",
  "workspace_id": "ws_your_workspace_id",
  "timestamp": "2026-07-07T13:00:00Z",
  "data": {
    "ticket_number": 43,
    "title": "Checkout button unresponsive on mobile",
    "description": "Tapping the checkout button on iOS Safari does nothing.",
    "category": "Bug",
    "priority": "High",
    "status": "Open",
    "reporter": "John Doe",
    "reporter_email": "john@example.com",
    "assignee": null,
    "assignee_email": null,
    "created_at": "2026-07-07T13:00:00Z"
  }
}
\`\`\`

### Example: ticket.resolved payload

\`\`\`json
{
  "event": "ticket.resolved",
  "workspace_id": "ws_your_workspace_id",
  "timestamp": "2026-07-07T14:00:00Z",
  "data": {
    "ticket_number": 43,
    "title": "Checkout button unresponsive on mobile",
    "status": "Resolved",
    "previous_status": "In Progress",
    "resolved_by": "Jane Smith",
    "resolved_by_email": "jane@example.com",
    "assignee": "Jane Smith",
    "resolved_at": "2026-07-07T14:00:00Z"
  }
}
\`\`\`

### Payload Structure

All webhook deliveries share the same envelope structure:

\`\`\`json
{
  "event": "ticket.created",
  "workspace_id": "ws_your_workspace_id",
  "timestamp": "2026-07-07T13:00:00Z",
  "data": { ... event-specific fields ... }
}
\`\`\`

### HTTP Headers

| Header | Description |
|--------|-------------|
| \`Content-Type\` | application/json |
| \`X-Kora-Event\` | The event name (e.g. ticket.created) |
| \`X-Kora-Delivery\` | Unique delivery ID for this webhook delivery |
| \`X-Kora-Signature\` | HMAC-SHA256 signature (only if a secret token is set) |

### Signature Verification

If you set a secret token when creating a webhook, Kora includes an \`X-Kora-Signature\` header with the v1 scheme: \`t=<unix-seconds>,v1=<hex HMAC-SHA256>\`. The signature covers \`\${t}.\${rawBody}\` — timestamp and raw body — so you can reject replayed deliveries (compare \`t\` against your own clock; a 5-minute tolerance is typical). \`X-Kora-Timestamp\` carries the same timestamp for convenience.

\`\`\`javascript
import crypto from 'crypto';

function verifySignature(rawBody, signatureHeader, secret, toleranceSec = 300) {
  const [tPart, v1Part] = signatureHeader.split(',');
  const t = tPart.replace('t=', '');
  const provided = v1Part.replace('v1=', '');
  if (Math.abs(Date.now() / 1000 - Number(t)) > toleranceSec) return false; // replay guard
  const expected = crypto
    .createHmac('sha256', secret)
    .update(\`\${t}.\${rawBody}\`)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(provided),
  );
}
\`\`\`

### Managing Webhooks

Webhooks are managed from **Settings → Webhooks** in the Kora dashboard. Only workspace owners and admins can create, edit, and delete webhooks.

**From the Dashboard**

- Navigate to Settings → Webhooks
- Enter your HTTPS endpoint URL and optional description
- Select which events the webhook should receive (all selected by default)
- Click "Add Webhook" to register the endpoint
- Use the "Test" button to send a test event and verify your endpoint
- View the delivery log to see recent delivery attempts and their status
- Pause or delete webhooks at any time

> **Warning:** Webhook URLs must use HTTPS. Up to 10 webhooks per workspace.

---

## Reference

### Ticket Object

| Field | Type | Description |
|-------|------|-------------|
| \`ticket_number\` | integer | Auto-incrementing ticket identifier, unique within a workspace. |
| \`title\` | string | Ticket title. |
| \`description\` | string \| null | Full description text. |
| \`category\` | string | AI-assigned or user-set category (e.g. Bug, Feature Request). |
| \`priority\` | string | Low \| Medium \| High \| Critical |
| \`status\` | string | Open \| Assigned \| In Progress \| Resolved \| Closed \| Reopened |
| \`reporter\` | string | Display name or email of the person who created the ticket. |
| \`assignee\` | string \| null | Display name or email of the assigned member. null if unassigned. |
| \`created_at\` | ISO 8601 | UTC timestamp when the ticket was created. |
| \`resolved_at\` | ISO 8601 \| null | UTC timestamp when resolved. null if still open. |
| \`updated_at\` | ISO 8601 | UTC timestamp when the ticket was last updated. |
| \`attachments\` | array | List of file attachments. Each has id, file_name, file_type, file_size, storage_url. |

### Comment Object

| Field | Type | Description |
|-------|------|-------------|
| \`id\` | integer | Unique comment identifier. |
| \`content\` | string | Comment body text. |
| \`parent_id\` | integer \| null | ID of the parent comment for threaded replies. null for top-level comments. |
| \`user\` | string | Display name or email of the comment author. |
| \`created_at\` | ISO 8601 | UTC timestamp when the comment was posted. |
| \`attachments\` | array | List of file attachments. Each has id, file_name, file_type, file_size (bytes), and storage_url. |

### Response Headers

All successful responses include rate limit headers and CORS headers to support browser-based integrations.

| Header | Description |
|--------|-------------|
| \`Content-Type\` | application/json |
| \`Access-Control-Allow-Origin\` | * — Public API endpoints are CORS-open. |
| \`X-RateLimit-Limit\` | Your plan's daily request cap. |
| \`X-RateLimit-Remaining\` | Requests you have left today. |
| \`X-RateLimit-Reset\` | Unix timestamp when the window resets. |

---

## Support

Growth and Custom plan workspaces can request custom API creation. Reach out at founder@usekora.app — we respond within 48 hours.

---

*Generated from [usekora.app/docs](https://usekora.app/docs)*
`;
}

// ─── Page ─────────────────────────────────────────────────

export function DocsPage() {
  const [activeSection, setActiveSection] = useState('overview');
  const [activeSubsection, setActiveSubsection] = useState<string | null>(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const originalTitle = useRef(document.title);

  function handleDownloadDocs() {
    const markdown = generateDocsMarkdown();
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kora-api-reference.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Scroll to hash target on mount (supports cross-page deep links)
  useEffect(() => {
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    // Defer until sections are painted
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(t);
  }, []);

  // Scroll-tracking observer — tracks both main sections and sub-headings
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            // Check if this is a sub-heading or a main section
            const isSubsection = Object.values(endpointSubsections).some((subs) =>
              subs.some((s) => s.id === id),
            );
            if (isSubsection) {
              setActiveSubsection(id);
            } else {
              setActiveSection(id);
              setActiveSubsection(null);
            }
          }
        });
      },
      { rootMargin: '-80px 0px -70% 0px' },
    );

    // Observe main sections
    tocSections.forEach((section) => {
      section.links.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    });

    // Observe sub-headings
    Object.values(endpointSubsections).forEach((subs) => {
      subs.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    });

    return () => observer.disconnect();
  }, []);

  // Dynamic <title>, meta description, canonical URL, and hash on scroll
  useEffect(() => {
    const meta = sectionMeta[activeSection];
    if (!meta) return;

    // Also resets robots to index,follow (the HTML shell defaults to noindex).
    setDocumentMeta(meta.title, meta.desc);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://usekora.app/docs');

    if (window.location.hash !== `#${activeSection}`) {
      history.replaceState(null, '', `#${activeSection}`);
    }

    return () => {
      document.title = originalTitle.current;
    };
  }, [activeSection]);

  // JSON-LD structured data (inject once on mount)
  useEffect(() => {
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'TechArticle',
      headline: 'Kora Public API Reference v1',
      description: 'REST API documentation for the Kora issue tracking platform. Endpoints for tickets, comments, and analytics.',
      author: { '@type': 'Organization', name: 'Kora' },
      publisher: { '@type': 'Organization', name: 'Kora' },
      url: 'https://usekora.app/docs',
      about: ['REST API', 'Issue Tracking', 'Ticket Management'],
      proficiencyLevel: 'Expert',
      dependencies: 'HTTPS, API Key authentication',
      teachinGMode: 'reference',
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Open Graph + Twitter card meta tags (inject once on mount)
  useEffect(() => {
    const tags: { property?: string; name?: string; content: string }[] = [
      { property: 'og:title', content: 'Kora Public API Reference v1' },
      { property: 'og:description', content: 'REST API for tickets, comments, and analytics. Authenticate with API keys.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://usekora.app/docs' },
      { property: 'og:site_name', content: 'Kora' },
      { name: 'twitter:card', content: 'summary' },
      { name: 'twitter:title', content: 'Kora Public API Reference v1' },
      { name: 'twitter:description', content: 'REST API for tickets, comments, and analytics.' },
    ];

    const els: HTMLElement[] = [];
    tags.forEach((tag) => {
      const el = document.createElement('meta');
      if (tag.property) el.setAttribute('property', tag.property);
      if (tag.name) el.setAttribute('name', tag.name);
      el.setAttribute('content', tag.content);
      document.head.appendChild(el);
      els.push(el);
    });

    return () => {
      els.forEach((el) => document.head.removeChild(el));
    };
  }, []);

  return (
    <div className="warm-page docs-warm min-h-screen flex flex-col">
      <MarketingNav />
      <div aria-hidden="true" className="h-16 shrink-0" />

      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-[80] bg-black/40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      <aside
        className={cn(
          'fixed left-0 top-16 bottom-0 w-[260px] z-[81] bg-[var(--warm-page)] border-r border-zinc-200 overflow-y-auto p-3 transition-transform duration-300 lg:hidden',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {sidebarSections.map((section) => (
          <div key={section.title} className="mb-4">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 px-3 py-2">
              {section.title}
            </p>
            {section.links.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={() => setMobileSidebarOpen(false)}
                className={cn(
                  'flex items-center px-3 py-1.5 rounded-lg text-xs transition-all',
                  activeSection === link.id
                    ? 'text-zinc-900 font-semibold bg-[var(--warm-card)]'
                    : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 ',
                )}
              >
                {link.method && (
                  <span
                    className={cn(
                      'inline-flex items-center justify-center w-9 h-5 rounded text-[10px] font-bold font-mono mr-2 shrink-0',
                      link.method === 'GET' && 'bg-green-100 text-green-700 ',
                      link.method === 'POST' && 'bg-blue-100 text-blue-700 ',
                      link.method === 'PATCH' && 'bg-amber-100 text-amber-700 ',
                    )}
                  >
                    {link.method}
                  </span>
                )}
                {link.label}
              </a>
            ))}
          </div>
        ))}
      </aside>

      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 py-8 sm:py-12 grid grid-cols-1 lg:grid-cols-[220px_1fr] xl:grid-cols-[220px_1fr_200px] gap-10">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block sticky top-20 self-start max-h-[calc(100vh-80px)] overflow-y-auto pr-2">
          {sidebarSections.map((section) => (
            <div key={section.title} className="mb-4">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 px-3 py-2">
                {section.title}
              </p>
              {section.links.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className={cn(
                    'flex items-center px-3 py-1.5 rounded-lg text-xs transition-all',
                    activeSection === link.id
                      ? 'text-zinc-900 font-semibold bg-[var(--warm-card)]'
                      : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 ',
                  )}
                >
                  {link.method && (
                    <span
                      className={cn(
                        'inline-flex items-center justify-center w-9 h-5 rounded text-[10px] font-bold font-mono mr-2 shrink-0',
                        link.method === 'GET' && 'bg-green-100 text-green-700 ',
                        link.method === 'POST' && 'bg-blue-100 text-blue-700 ',
                        link.method === 'PATCH' && 'bg-amber-100 text-amber-700 ',
                      )}
                    >
                      {link.method}
                    </span>
                  )}
                  {link.label}
                </a>
              ))}
            </div>
          ))}
        </aside>

        {/* Main content */}
        <main className="min-w-0">
          {/* Mobile toolbar */}
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 "
            >
              <Menu className="size-4" />
              Menu
            </button>
            <DocsSearch sections={sidebarSections} />
          </div>

          {/* Desktop search */}
          <div className="hidden lg:flex justify-end mb-2">
            <DocsSearch sections={sidebarSections} />
          </div>

          {/* Page title */}
          <div className="mb-8">
            <p className="warm-eyebrow mb-4">Developers</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] text-zinc-900 mb-4">
              API Reference
            </h1>
            <p className="text-zinc-500 text-base sm:text-lg max-w-2xl mb-6">
              Integrate your Kora workspace data into dashboards, automations, and internal tools
              with the Kora Public API.
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { label: 'v1', tone: 'bg-emerald-100 text-emerald-700 ' },
                { label: 'Stable', tone: 'bg-blue-100 text-blue-700 ' },
                { label: 'REST', tone: 'bg-zinc-100 text-zinc-600 ' },
                { label: 'JSON', tone: 'bg-zinc-100 text-zinc-600 ' },
                { label: 'API Key Auth', tone: 'bg-zinc-100 text-zinc-600 ' },
              ].map((pill) => (
                <span
                  key={pill.label}
                  className={cn(
                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold',
                    pill.tone,
                  )}
                >
                  {pill.label}
                </span>
              ))}
              <button
                onClick={handleDownloadDocs}
                className="inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 transition-all shrink-0 ml-auto"
              >
                <Download className="size-3.5" strokeWidth={1.8} />
                Download .md
              </button>
            </div>
          </div>

          {/* Changelog banner */}
          <ChangelogBanner />

          {/* Overview */}
          <DocCard id="overview" title="Overview">
            <p className="text-zinc-600 text-sm leading-relaxed mb-4">
              The Kora Public API is a workspace-scoped REST API that gives programmatic access to
              tickets, comments, and analytics. All requests are authenticated via API key headers
              and return JSON.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: 'Format', value: 'JSON' },
                { label: 'Version', value: 'v1' },
                { label: 'Auth', value: 'API Key (headers)' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-zinc-200 p-4 bg-zinc-50/50 "
                >
                  <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-1">
                    {item.label}
                  </p>
                  <p className="text-sm font-semibold text-zinc-900 ">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </DocCard>

          {/* Quick Start */}
          <DocCard id="quick-start" title="Quick Start">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              Make your first request in 30 seconds. Replace{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">ws_abc123</code>{' '}
              and{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">sk_xyz456</code>{' '}
              with your workspace ID and secret key from{' '}
              <strong>Settings &rarr; API Access</strong>.
            </p>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl https://api.usekora.app/public/v1/tickets \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456" \\
  -G \\
  --data-urlencode "start_date=2026-06-01" \\
  --data-urlencode "end_date=2026-06-30"` },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets?start_date=2026-06-01&end_date=2026-06-30", {
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456"
  }
});
const data = await res.json();
console.log(data);` },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.get(
    "https://api.usekora.app/public/v1/tickets",
    params={"start_date": "2026-06-01", "end_date": "2026-06-30"},
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/tickets?start_date=2026-06-01&end_date=2026-06-30", nil)
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
body, _ := io.ReadAll(res.Body)
fmt.Println(string(body))` },
            ]} />
            <p className="text-zinc-500 text-sm">
              That's it. You'll get a JSON response with your workspace's tickets. Explore the endpoints below for more.
            </p>
          </DocCard>

          {/* Base URL */}
          <DocCard id="base-url" title="Base URL">
            <CodeBlock copyText="https://api.usekora.app/public/v1">
              https://api.usekora.app/public/v1
            </CodeBlock>
            <p className="text-zinc-500 text-sm">
              All endpoints are prefixed with{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">
                /public/v1
              </code>
              . The Kora Dashboard API (used internally by the app) is separate and not accessible
              via Public API keys.
            </p>
          </DocCard>

          {/* Authentication */}
          <DocCard id="authentication" title="Authentication">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              Every request must include two headers identifying your workspace and proving your
              identity.
            </p>
            <ParamTable
              headers={['Header', 'Required', 'Description']}
              rows={[
                [
                  { mono: true, text: 'X-Workspace-ID' },
                  { req: true, text: '' },
                  "Your workspace's unique identifier. Find it in Settings → API inside the Kora app.",
                ],
                [
                  { mono: true, text: 'X-Secret-Key' },
                  { req: true, text: '' },
                  "Your workspace's secret API key. Treat this like a password — never expose it in client-side code.",
                ],
              ]}
            />
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `# Example request with auth headers
curl https://api.usekora.app/public/v1/tickets \\
  -H "X-Workspace-ID: ws_your_workspace_id" \\
  -H "X-Secret-Key: sk_your_secret_key" \\
  -G \\
  --data-urlencode "start_date=2026-06-01" \\
  --data-urlencode "end_date=2026-06-30"` },
              {
                label: 'JavaScript', language: 'javascript', code: `// Example request with auth headers
const res = await fetch("https://api.usekora.app/public/v1/tickets?start_date=2026-06-01&end_date=2026-06-30", {
  headers: {
    "X-Workspace-ID": "ws_your_workspace_id",
    "X-Secret-Key": "sk_your_secret_key"
  }
});
const data = await res.json();` },
              {
                label: 'Python', language: 'python', code: `# Example request with auth headers
import requests

res = requests.get(
    "https://api.usekora.app/public/v1/tickets",
    params={"start_date": "2026-06-01", "end_date": "2026-06-30"},
    headers={
        "X-Workspace-ID": "ws_your_workspace_id",
        "X-Secret-Key": "sk_your_secret_key"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/tickets?start_date=2026-06-01&end_date=2026-06-30", nil)
req.Header.Set("X-Workspace-ID", "ws_your_workspace_id")
req.Header.Set("X-Secret-Key", "sk_your_secret_key")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
body, _ := io.ReadAll(res.Body)
fmt.Println(string(body))` },
            ]} />
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
              <AlertTriangle className="size-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 ">
                Keep your <code className="text-xs bg-amber-100 px-1 rounded">X-Secret-Key</code>{' '}
                private. If compromised, regenerate it from Settings → API in the Kora app.
              </p>
            </div>
          </DocCard>

          {/* Automation Rules */}
          <DocCard id="automations-overview" title="Automation Rules">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              If-this-then-that rules that listen to ticket events and execute actions. Free on every workspace.
              Includes triggers (ticket created, status changed, SLA breached, no response for N hours),
              conditions (category, priority, reporter email, ticket source), and actions (set priority/status,
              assign, add comment, fire webhook, send email to members, re-run AI structuring).
            </p>
            <ParamTable
              headers={['Endpoint', 'Auth', 'Description']}
              rows={[
                [{ mono: true, text: 'GET /workspace/:id/automations' }, 'Member session', 'List rules + recent runs (viewer read-only)'],
                [{ mono: true, text: 'POST /workspace/:id/automations' }, 'Owner/Admin/Manager', 'Create a rule'],
                [{ mono: true, text: 'POST /workspace/:id/automations/test' }, 'Owner/Admin/Manager', 'Dry-run a draft rule (zero side effects)'],
                [{ mono: true, text: 'PUT /workspace/:id/automations-order' }, 'Owner/Admin/Manager', 'Reorder rules (array of IDs)'],
                [{ mono: true, text: 'PUT /workspace/:id/automations/:id' }, 'Owner/Admin/Manager', 'Update a rule'],
                [{ mono: true, text: 'DELETE /workspace/:id/automations/:id' }, 'Owner/Admin/Manager', 'Soft-delete (run logs preserved)'],
                [{ mono: true, text: 'GET /workspace/:id/automations/:id/runs' }, 'Member session', 'Run history (paginated)'],
                [{ mono: true, text: 'GET/PUT /workspace/:id/automations/settings' }, 'Owner/Admin (write)', 'Workspace automation toggle'],
              ]}
            />
            <p className="text-zinc-500 text-sm mt-5">
              Rate caps: 500 rule executions/hour per workspace, 100/hour per rule, 50 AI structuring actions/day.
              Automation comments never satisfy SLA first-response. Automated emails go to workspace members only
              (never to ticket reporters — spam guard).
            </p>
          </DocCard>

          {/* Helpdesk */}
          <DocCard id="helpcenter-overview" title="Helpdesk API">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              A blog-style knowledge base: articles publish to a branded public helpdesk at{' '}
              <span className="font-mono text-xs">/p/helpdesk/&lt;link&gt;</span>. Publishing costs 1 credit —
              once per article; edits, unpublish/republish, search, votes and subscriptions are free. Writes need
              Owner/Admin/Manager; reads are open to every member; settings and link claims need Owner/Admin.
            </p>
            <ParamTable
              headers={['Endpoint', 'Auth', 'Description']}
              rows={[
                [{ mono: true, text: 'GET /workspace/:id/kb/articles' }, 'Member session', 'List articles (status filter: draft/published/trash, search q, pagination)'],
                [{ mono: true, text: 'POST /workspace/:id/kb/articles' }, 'Owner/Admin/Manager', 'Create a draft'],
                [{ mono: true, text: 'GET /workspace/:id/kb/articles/:aid' }, 'Member session', 'Read one (includes draft-asset signatures)'],
                [{ mono: true, text: 'PUT /workspace/:id/kb/articles/:aid' }, 'Owner/Admin/Manager', 'Edit — free, creates a revision'],
                [{ mono: true, text: 'POST /workspace/:id/kb/articles/:aid/publish' }, 'Owner/Admin/Manager', 'Publish — 1 credit, charged once per article (402 when wallet is at floor)'],
                [{ mono: true, text: 'POST /workspace/:id/kb/articles/:aid/unpublish' }, 'Owner/Admin/Manager', 'Back to draft — free'],
                [{ mono: true, text: 'POST /workspace/:id/kb/articles/:aid/share' }, 'Owner/Admin/Manager', 'Create a 7-day draft link (rotate revokes)'],
                [{ mono: true, text: 'GET /workspace/:id/kb/articles/:aid/revisions' }, 'Member session', 'Last 50 revisions; restore via POST /revisions/:rid/restore'],
                [{ mono: true, text: 'GET/PUT /workspace/:id/kb/settings' }, 'Owner/Admin (write)', 'Visibility, branding, fonts, subscription toggle'],
                [{ mono: true, text: 'GET/POST /workspace/:id/kb/slugs' }, 'Owner/Admin (claim)', 'Claim custom links (max 5, permanent); POST /kb/slugs/:id/primary switches'],
                [{ mono: true, text: 'GET /workspace/:id/kb/analytics?days=' }, 'Owner/Admin/Manager', 'Visitors, pageviews, referrers, top articles, searches'],
                [{ mono: true, text: 'GET /workspace/:id/kb/subscribers(.csv)' }, 'Owner/Admin/Manager', 'Subscriber list + CSV export; DELETE /:sub_id removes'],
              ]}
            />
            <p className="text-zinc-500 text-sm mt-5">
              Public, no auth: <span className="font-mono text-xs">GET /public/v1/kb/:ident</span> (listing),{' '}
              <span className="font-mono text-xs">/articles/:slug</span>,{' '}
              <span className="font-mono text-xs">/search?q=</span>,{' '}
              <span className="font-mono text-xs">/subscribe</span>,{' '}
              <span className="font-mono text-xs">/track</span> (analytics beacon),{' '}
              <span className="font-mono text-xs">/vote</span>, media serving with HTTP Range for video, and{' '}
              <span className="font-mono text-xs">/public/v1/kb/directory</span>. Secret-key API:{' '}
              <span className="font-mono text-xs">GET /public/v1/articles</span> and{' '}
              <span className="font-mono text-xs">GET /public/v1/articles/:slug</span> (published articles of a
              public helpdesk). Media uploads use the existing upload endpoint with{' '}
              <span className="font-mono text-xs">attachment_type: "kb"</span> (images and MP4/WebM; bytes meter
              into storage billing). Webhook events: <span className="font-mono text-xs">article.published</span>,{' '}
              <span className="font-mono text-xs">article.updated</span>.
            </p>
          </DocCard>

          {/* Categories */}
          <DocCard id="categories-overview" title="Categories API">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              Ticket categories are a managed per-workspace list. <strong className="text-zinc-900 ">"Other"</strong> is a
              permanent system category. Unknown categories submitted on ticket creation are auto-created
              (case-insensitive, max 50 chars). Renaming a category updates every ticket that uses it.
            </p>
            <ParamTable
              headers={['Endpoint', 'Auth', 'Description']}
              rows={[
                [{ mono: true, text: 'GET /workspace/:id/categories' }, 'Member session', 'Managed list with ticket counts + ai_auto_category flag'],
                [{ mono: true, text: 'POST /workspace/:id/categories' }, 'Owner/Admin', 'Create a category (409 on duplicate)'],
                [{ mono: true, text: 'PUT /workspace/:id/categories/:id' }, 'Owner/Admin', 'Rename — cascades to all tickets carrying the old name'],
                [{ mono: true, text: 'DELETE /workspace/:id/categories/:id' }, 'Owner/Admin', 'Delete — blocked while tickets use it; system categories never'],
                [{ mono: true, text: 'GET/PUT /workspace/:id/categories/settings' }, 'Owner/Admin (write)', 'The "Let AI create categories" toggle'],
                [{ mono: true, text: 'GET /public/v1/categories' }, 'X-Workspace-ID + X-Secret-Key', 'Public list with ticket counts'],
              ]}
            />
            <p className="text-zinc-500 text-sm mt-5">
              AI structuring picks from this list. With the AI toggle on, unmatched tickets get a newly created category;
              with it off, they fall back to <strong>Other</strong>.
            </p>
          </DocCard>

          {/* Credits & Billing */}
          <DocCard id="credits-overview" title="Credits & Billing">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              Kora is pay-as-you-go: workspaces hold a prepaid credit wallet.{' '}
              <strong className="text-zinc-900 ">$1 = 10 credits</strong>. Creating a
              ticket costs <strong>1 credit</strong> (any channel, including the Public API), and one
              successful AI Analyst answer costs <strong>1 credit</strong>. Members, comments,
              attachments, AI ticket structuring, webhooks, and embedded sessions are free.
            </p>
            <ParamTable
              headers={['Action', 'Credits', 'Charged when']}
              rows={[
                ['Ticket created', '1', 'After the ticket is successfully created'],
                ['AI Analyst answer', '1', 'Only on a successful answer — failures are free'],
                ['Magic Helpdesk draft run', '10', 'AI scans your website and drafts helpdesk articles; refunded if nothing is drafted'],
                ['Public API responses', '1 per 400', 'Automatically as responses are served'],
                ['Top-up', 'Pack size', 'After Dodo confirms the payment (webhook)'],
              ]}
            />
            <p className="text-zinc-600 text-sm leading-relaxed mt-5 mb-3">
              New workspaces can claim <strong>1,000 bonus credits</strong> (30-day validity) plus{' '}
              <strong>100 base credits</strong> (10-year validity) from the dashboard — no card
              required. Top-up packs start at <strong>$5</strong> ($5 / $10 / $20 / $50 / $100 or custom),
              with <strong>+30% bonus credits on the first recharge</strong>. Purchased credits are valid for
              10 years. The wallet may overdraw down to <strong>−10 credits</strong>; below that,
              ticket creation and AI answers return <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">402</code> with{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">code: &quot;CREDITS_EXHAUSTED&quot;</code> until
              the workspace tops up. Reading data is never blocked.
            </p>
            <p className="text-zinc-600 text-sm leading-relaxed mb-3">
              Wallet endpoints (dashboard auth):
            </p>
            <ParamTable
              headers={['Endpoint', 'Description']}
              rows={[
                [{ mono: true, text: 'GET /workspace/:id/credits' }, 'Balance, grants with expiry, month-to-date usage, recent ledger'],
                [{ mono: true, text: 'POST /workspace/:id/credits/claim' }, 'Legacy. Signup credits are now granted automatically on the first dashboard load; a repeat returns ALREADY_CLAIMED'],
                [{ mono: true, text: 'POST /workspace/:id/credits/checkout' }, 'Create a Dodo checkout for a credit pack (owner/admin)'],
                [{ mono: true, text: 'GET /workspace/:id/credits/invoices' }, 'Top-up history with Dodo invoice download links (owner/admin)'],
              ]}
            />
            <p className="text-zinc-500 text-sm mt-5">
              Every credit movement is written to an append-only ledger with an idempotency key —
              retries never double-charge, and the cached balance is continuously reconciled against
              the ledger.
            </p>
          </DocCard>

          {/* Rate Limits */}
          <DocCard id="rate-limits" title="Rate Limits">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              Rate limits are applied per workspace, across all Public API endpoints. Dashboard app
              usage does not count toward the limit. With pay-as-you-go credits, the daily tier
              follows the wallet: a funded wallet gets the high tier, an empty wallet the low tier.
            </p>
            <ParamTable
              headers={['Wallet state', 'Daily limit', 'Grace buffer']}
              rows={[
                ['Credit balance ≤ 0', '5,000 requests / day', '+5%'],
                ['Credit balance > 0', '50,000 requests / day', '+5%'],
                ['Custom api_rate_limit set', 'Configured value', '+5%'],
              ]}
            />
            <p className="text-zinc-600 text-sm mb-3">
              Every response includes these headers so you can track your usage:
            </p>
            <ParamTable
              headers={['Header', 'Description']}
              rows={[
                [{ mono: true, text: 'X-RateLimit-Limit' }, "Your plan's daily request cap"],
                [{ mono: true, text: 'X-RateLimit-Remaining' }, 'Requests remaining in the current window'],
                [{ mono: true, text: 'X-RateLimit-Reset' }, 'Unix timestamp when the window resets'],
              ]}
            />
            <p className="text-zinc-500 text-sm">
              When the limit is exceeded, the API returns{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">429 Too Many Requests</code>{' '}
              with a <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">retryAfter</code> field
              indicating when to retry.
            </p>
          </DocCard>

          {/* Errors */}
          <DocCard id="errors" title="Errors">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              All errors return a JSON body with{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">success: false</code>,
              a human-readable{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">error</code> string,
              and the HTTP{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">code</code>.
            </p>
            <ParamTable
              headers={['Status', 'Meaning']}
              rows={[
                [{ mono: true, text: '400' }, 'Bad request — missing or invalid parameters'],
                [{ mono: true, text: '401' }, 'Unauthorised — invalid or missing API key headers'],
                [{ mono: true, text: '404' }, "Not found — the requested resource doesn't exist in your workspace"],
                [{ mono: true, text: '429' }, 'Rate limit exceeded — slow down and retry after the reset window'],
                [{ mono: true, text: '500' }, 'Internal server error — contact founder@usekora.app'],
              ]}
            />
            <CodeBlock language="json">{`// Error response shape
{
  "success": false,
  "error": "start_date and end_date are required",
  "code": 400
}`}</CodeBlock>
          </DocCard>

          {/* ── WORKSPACE ── */}

          {/* Project Summary */}
          <EndpointCard
            id="project-summary"
            method="GET"
            path="/workspace"
            description="Get a summary of your workspace with 20 key configuration fields."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns workspace metadata, plan details, workflow configuration, limits, and live counts
              (members, tickets, webhooks). Useful for building custom dashboards or validating workspace
              configuration before performing operations.
            </p>
            <SectionTitle>Response Fields</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 ">
                    <th className="text-left py-2 pr-4 font-medium text-zinc-500 ">Field</th>
                    <th className="text-left py-2 pr-4 font-medium text-zinc-500 ">Type</th>
                    <th className="text-left py-2 font-medium text-zinc-500 ">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 ">
                  {[
                    ['workspace_id', 'string', 'Unique workspace identifier (e.g. ws_abc123)'],
                    ['name', 'string', 'Workspace display name'],
                    ['logo_url', 'string|null', 'Workspace logo URL'],
                    ['status', 'string', 'Workspace status: active, suspended, etc.'],
                    ['ticket_workflow', 'string', 'Workflow mode: claim_based, assignment_based, auto_claim'],
                    ['auto_close_mode', 'string', 'Auto-close mode: disabled, instant, delayed'],
                    ['auto_close_delay_days', 'integer', 'Days before auto-closing resolved tickets'],
                    ['ticket_history_days', 'integer', 'Ticket retention period in days'],
                    ['max_file_size_mb', 'integer', 'Maximum file size per upload in MB'],
                    ['file_storage_limit_mb', 'number', 'Total file storage limit in MB'],
                    ['api_rate_limit', 'string', 'Configured API rate limit description (e.g. 5000/day)'],
                    ['members_count', 'integer', 'Current active member count'],
                    ['tickets_count', 'integer', 'Total tickets in workspace'],
                    ['webhooks_count', 'integer', 'Active webhooks count'],
                    ['created_at', 'datetime', 'Workspace creation timestamp'],
                    ['activation_date', 'datetime|null', 'When the workspace was activated'],
                  ].map(([field, type, desc]) => (
                    <tr key={field}>
                      <td className="py-2 pr-4 font-mono text-xs text-blue-600 ">{field}</td>
                      <td className="py-2 pr-4 font-mono text-xs text-zinc-500 ">{type}</td>
                      <td className="py-2 text-zinc-600 text-xs">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s "https://api.usekora.app/public/v1/workspace" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456"` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/workspace", {\n  headers: {\n    "X-Workspace-ID": "ws_abc123",\n    "X-Secret-Key": "sk_xyz456"\n  }\n});\nconst data = await res.json();` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.get(\n    "https://api.usekora.app/public/v1/workspace",\n    headers={\n        "X-Workspace-ID": "ws_abc123",\n        "X-Secret-Key": "sk_xyz456"\n    }\n)\nprint(res.json())` },
              { label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/workspace", nil)\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")\nres, _ := http.DefaultClient.Do(req)\ndefer res.Body.Close()\nbody, _ := io.ReadAll(res.Body)\nfmt.Println(string(body))` },
            ]} />
            <SectionTitle id="project-summary-response">Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "workspace": {
    "workspace_id": "ws_abc123",
    "name": "Acme Inc",
    "logo_url": "https://files.usekora.app/logos/ws_abc123_logo.png",
    "status": "active",
    "ticket_workflow": "claim_based",
    "auto_close_mode": "disabled",
    "auto_close_delay_days": 7,
    "ticket_history_days": 365,
    "max_file_size_mb": 100,
    "file_storage_limit_mb": 1024,
    "api_rate_limit": "50000/day",
    "members_count": 5,
    "tickets_count": 142,
    "webhooks_count": 3,
    "created_at": "2026-06-01 10:00:00",
    "activation_date": "2026-06-01 10:00:00"
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* ── TICKETS ── */}

          {/* List Tickets */}
          <EndpointCard
            id="list-tickets"
            method="GET"
            path="/tickets"
            description="List and filter tickets within a date range."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns a paginated list of tickets created within the specified date range. Maximum range is{' '}
              <strong>92 days</strong>. Results are ordered newest first. Use{' '}
              <code className="text-xs">page</code> and{' '}
              <code className="text-xs">limit</code> query parameters for pagination (max 100 per page).
            </p>
            <SectionTitle id="list-tickets-query-parameters">Query Parameters</SectionTitle>
            <ParamTable
              headers={['Parameter', 'Type', 'Required', 'Description']}
              rows={[
                [{ mono: true, text: 'start_date' }, 'string', { req: true, text: '' }, 'Start of date range. Format: YYYY-MM-DD'],
                [{ mono: true, text: 'end_date' }, 'string', { req: true, text: '' }, 'End of date range. Format: YYYY-MM-DD. Max 92 days from start_date.'],
                [{ mono: true, text: 'reporter_email' }, 'string', { req: false, text: '' }, "Filter by the reporter's email address."],
                [{ mono: true, text: 'page' }, 'integer', { req: false, text: '' }, 'Page number (default: 1).'],
                [{ mono: true, text: 'limit' }, 'integer', { req: false, text: '' }, 'Items per page (default: 100, max: 100).'],
              ]}
            />
            <SectionTitle id="list-tickets-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl https://api.usekora.app/public/v1/tickets \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456" \\
  -G \\
  --data-urlencode "start_date=2026-06-01" \\
  --data-urlencode "end_date=2026-06-30"` },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets?start_date=2026-06-01&end_date=2026-06-30", {
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456"
  }
});
const data = await res.json();` },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.get(
    "https://api.usekora.app/public/v1/tickets",
    params={"start_date": "2026-06-01", "end_date": "2026-06-30"},
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/tickets?start_date=2026-06-01&end_date=2026-06-30", nil)
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
body, _ := io.ReadAll(res.Body)
fmt.Println(string(body))` },
            ]} />
            <SectionTitle id="list-tickets-response">Response</SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "workspace_id": "ws_abc123",
  "request_range": {
    "start_date": "2026-06-01",
    "end_date": "2026-06-30",
    "duration_days": 29
  },
  "tickets": [
    {
      "ticket_number": 42,
      "title": "Login page throws 500 on Safari",
      "description": "Reproducible on Safari 17.x — ...",
      "category": "Bug",
      "priority": "High",
      "status": "Open",
      "reporter": "John Doe",
      "assignee": "Jane Smith",
      "created_at": "2026-06-15T09:41:22Z",
      "resolved_at": null
    }
  ],
  "total_count": 1,
  "returned_count": 1,
  "pagination": {
    "page": 1,
    "limit": 100,
    "total": 1,
    "total_pages": 1,
    "has_next": false
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* Create Ticket */}
          <EndpointCard
            id="create-ticket"
            method="POST"
            path="/tickets"
            description="Create a new ticket in your workspace."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Creates a new ticket with status{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">Open</code>.
              The <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">reporter_email</code>{' '}
              must be an active workspace member.
            </p>
            <SectionTitle id="create-ticket-request-body">Request Body</SectionTitle>
            <ParamTable
              headers={['Field', 'Type', 'Required', 'Description']}
              rows={[
                [{ mono: true, text: 'title' }, 'string', { req: true, text: '' }, 'Short title for the ticket.'],
                [{ mono: true, text: 'description' }, 'string', { req: true, text: '' }, 'Full description of the issue.'],
                [{ mono: true, text: 'category' }, 'string', { req: true, text: '' }, 'Category label, e.g. Bug, Feature Request, Incident. Free-form string.'],
                [{ mono: true, text: 'priority' }, 'string', { req: true, text: '' }, 'One of: Low, Medium, High, Critical'],
                [{ mono: true, text: 'reporter_email' }, 'string', { req: true, text: '' }, 'Email of the reporter. Must be an active workspace member.'],
              ]}
            />
            <SectionTitle id="create-ticket-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl -X POST https://api.usekora.app/public/v1/tickets \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Checkout button unresponsive on mobile",
    "description": "Tapping the checkout button on iOS Safari does nothing.",
    "category": "Bug",
    "priority": "High",
    "reporter_email": "john@example.com"
  }'` },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets", {
  method: "POST",
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    title: "Checkout button unresponsive on mobile",
    description: "Tapping the checkout button on iOS Safari does nothing.",
    category: "Bug",
    priority: "High",
    reporter_email: "john@example.com"
  })
});
const data = await res.json();` },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.post(
    "https://api.usekora.app/public/v1/tickets",
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456",
        "Content-Type": "application/json"
    },
    json={
        "title": "Checkout button unresponsive on mobile",
        "description": "Tapping the checkout button on iOS Safari does nothing.",
        "category": "Bug",
        "priority": "High",
        "reporter_email": "john@example.com"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `body := map[string]interface{}{
    "title": "Checkout button unresponsive on mobile",
    "description": "Tapping the checkout button on iOS Safari does nothing.",
    "category": "Bug",
    "priority": "High",
    "reporter_email": "john@example.com",
}
jsonData, _ := json.Marshal(body)
req, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/tickets", bytes.NewBuffer(jsonData))
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
req.Header.Set("Content-Type", "application/json")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
resBody, _ := io.ReadAll(res.Body)
fmt.Println(string(resBody))` },
            ]} />
            <SectionTitle id="create-ticket-response">Response <span className="text-zinc-400 font-normal text-xs ml-1">201 Created</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "workspace_id": "ws_abc123",
  "ticket": {
    "ticket_number": 43,
    "title": "Checkout button unresponsive on mobile",
    "description": "Tapping the checkout button on iOS Safari does nothing.",
    "category": "Bug",
    "priority": "High",
    "status": "Open",
    "reporter": "John Doe",
    "assignee": null,
    "created_at": "2026-06-19T18:30:00Z"
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* Get Ticket */}
          <EndpointCard
            id="get-ticket"
            method="GET"
            path="/tickets/:ticket_id"
            description="Get a single ticket with full details and attachments."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns the full ticket details including reporter, assignee, and attachments. The{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">:ticket_id</code> is
              the ticket's numeric <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">ticket_number</code>.
            </p>
            <SectionTitle id="get-ticket-path-parameters">Path Parameters</SectionTitle>
            <ParamTable
              headers={['Parameter', 'Type', 'Required', 'Description']}
              rows={[
                [{ mono: true, text: 'ticket_id' }, 'integer', { req: true, text: '' }, "The ticket's numeric identifier (ticket_number), e.g. 42."],
              ]}
            />
            <SectionTitle id="get-ticket-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl https://api.usekora.app/public/v1/tickets/42 \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456"` },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets/42", {
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456"
  }
});
const data = await res.json();` },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.get(
    "https://api.usekora.app/public/v1/tickets/42",
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/tickets/42", nil)
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
body, _ := io.ReadAll(res.Body)
fmt.Println(string(body))` },
            ]} />
            <SectionTitle id="get-ticket-response">Response</SectionTitle>
            <CodeBlock language="json">{`{
              "success": true,
              "workspace_id": "ws_abc123",
              "ticket": {
                "ticket_number": 42,
              "title": "Login page throws 500 on Safari",
              "description": "Reproducible on Safari 17.x — ...",
              "category": "Bug",
              "priority": "High",
              "status": "Open",
              "reporter": "John Doe",
              "assignee": "Jane Smith",
              "created_at": "2026-06-15T09:41:22Z",
              "resolved_at": null,
              "updated_at": "2026-06-15T09:41:22Z",
              "attachments": []
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* Update Ticket */}
          <EndpointCard
            id="update-ticket"
            method="PATCH"
            path="/tickets/:ticket_id"
            description="Partially update a ticket's status, priority, or content."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-4">
              All fields are optional — only the fields you send are changed. Uses the ticket's{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">ticket_number</code> as
              the path parameter.
            </p>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 flex gap-3 mb-6">
              <Info className="size-5 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800 ">
                <strong>Status transitions</strong> are validated. You can only move to a status
                that is reachable from the current one. Invalid transitions return{' '}
                <code className="text-xs bg-blue-100 px-1 rounded">400</code> with
                the list of allowed next statuses.
              </p>
            </div>
            <SectionTitle id="update-ticket-status-transition-map">Status Transition Map</SectionTitle>
            <ParamTable
              headers={['Current status', 'Allowed transitions']}
              rows={[
                [{ mono: true, text: 'Open' }, 'Assigned, In Progress, Resolved, Closed'],
                [{ mono: true, text: 'Assigned' }, 'In Progress, Waiting for User, Waiting for Team, Resolved, Closed'],
                [{ mono: true, text: 'In Progress' }, 'Waiting for User, Waiting for Team, Resolved, Closed'],
                [{ mono: true, text: 'Waiting for User' }, 'In Progress, Resolved, Closed'],
                [{ mono: true, text: 'Waiting for Team' }, 'In Progress, Resolved, Closed'],
                [{ mono: true, text: 'Resolved' }, 'Closed, Reopened'],
                [{ mono: true, text: 'Closed' }, 'Reopened'],
                [{ mono: true, text: 'Reopened' }, 'Assigned, In Progress, Resolved, Closed'],
              ]}
            />
            <SectionTitle id="update-ticket-request-body">Request Body</SectionTitle>
            <ParamTable
              headers={['Field', 'Type', 'Required', 'Description']}
              rows={[
                [{ mono: true, text: 'status' }, 'string', { req: false, text: '' }, 'New status. Must be a valid transition from current status.'],
                [{ mono: true, text: 'priority' }, 'string', { req: false, text: '' }, 'One of: Low, Medium, High, Critical'],
                [{ mono: true, text: 'title' }, 'string', { req: false, text: '' }, 'Updated ticket title.'],
                [{ mono: true, text: 'description' }, 'string', { req: false, text: '' }, 'Updated ticket description.'],
                [{ mono: true, text: 'category' }, 'string', { req: false, text: '' }, 'Updated category label.'],
                [{ mono: true, text: 'assignee_email' }, 'string', { req: false, text: '' }, 'Email of an active workspace member to assign.'],
              ]}
            />
            <SectionTitle id="update-ticket-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl -X PATCH https://api.usekora.app/public/v1/tickets/43 \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456" \\
  -H "Content-Type: application/json" \\
  -d '{
    "status": "In Progress",
    "priority": "Critical",
    "assignee_email": "jane@example.com"
  }'` },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets/43", {
  method: "PATCH",
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    status: "In Progress",
    priority: "Critical",
    assignee_email: "jane@example.com"
  })
});
const data = await res.json();` },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.patch(
    "https://api.usekora.app/public/v1/tickets/43",
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456",
        "Content-Type": "application/json"
    },
    json={
        "status": "In Progress",
        "priority": "Critical",
        "assignee_email": "jane@example.com"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `body := map[string]interface{}{
    "status": "In Progress",
    "priority": "Critical",
    "assignee_email": "jane@example.com",
}
jsonData, _ := json.Marshal(body)
req, _ := http.NewRequest("PATCH", "https://api.usekora.app/public/v1/tickets/43", bytes.NewBuffer(jsonData))
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
req.Header.Set("Content-Type", "application/json")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
resBody, _ := io.ReadAll(res.Body)
fmt.Println(string(resBody))` },
            ]} />
            <SectionTitle id="update-ticket-response">Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
              "success": true,
              "workspace_id": "ws_abc123",
              "ticket": {
                "ticket_number": 43,
              "title": "Checkout button unresponsive on mobile",
              "description": "Tapping the checkout button on iOS Safari does nothing.",
              "category": "Bug",
              "priority": "Critical",
              "status": "In Progress",
              "reporter": "John Doe",
              "assignee": "Jane Smith",
              "created_at": "2026-06-19T18:30:00Z",
              "resolved_at": null,
              "updated_at": "2026-06-19T19:00:00Z"
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* Claim Ticket */}
          <EndpointCard
            id="claim-ticket"
            method="POST"
            path="/tickets/:ticket_id/claim"
            description="Claim a ticket on behalf of a workspace member."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Available only in <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">claim_based</code> and{' '}
              <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">auto_claim</code> workflows.
              Sets the ticket status to <code className="text-xs">Assigned</code> (claim_based) or{' '}
              <code className="text-xs">In Progress</code> (auto_claim).
            </p>
            <SectionTitle>Request Body</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 "><th className="text-left py-2 pr-4 font-medium text-zinc-500">Field</th><th className="text-left py-2 pr-4 font-medium text-zinc-500">Type</th><th className="text-left py-2 font-medium text-zinc-500">Description</th></tr></thead>
                <tbody className="divide-y divide-zinc-100 ">
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">claimer_email</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">Email of the member claiming the ticket (required)</td></tr>
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s -X POST "https://api.usekora.app/public/v1/tickets/42/claim" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456" \\\n  -H "Content-Type: application/json" \\\n  -d '{"claimer_email": "agent@acme.com"}'` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets/42/claim", {\n  method: "POST",\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json" },\n  body: JSON.stringify({ claimer_email: "agent@acme.com" })\n});\nconst data = await res.json();` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.post(\n    "https://api.usekora.app/public/v1/tickets/42/claim",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json"},\n    json={"claimer_email": "agent@acme.com"}\n)\nprint(res.json())` },
              { label: 'Go', language: 'go', code: `body := map[string]string{"claimer_email": "agent@acme.com"}\njsonData, _ := json.Marshal(body)\nreq, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/tickets/42/claim", bytes.NewBuffer(jsonData))\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")\nreq.Header.Set("Content-Type", "application/json")\nres, _ := http.DefaultClient.Do(req)\ndefer res.Body.Close()` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "ticket": {
    "ticket_number": 42,
    "title": "Login page crash",
    "status": "Assigned",
    "claimed_by": "Jane Smith",
    "claimed_at": "2026-08-22T10:30:00.000Z"
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* Merge Ticket */}
          <EndpointCard
            id="merge-ticket"
            method="POST"
            path="/tickets/:ticket_id/merge"
            description="Merge a ticket into another ticket. Moves comments, reactions, and attachments."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              The source ticket is closed and linked to the target. All comments, reactions, and attachments
              are moved to the target ticket. This operation is irreversible.
            </p>
            <SectionTitle>Request Body</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 "><th className="text-left py-2 pr-4 font-medium text-zinc-500">Field</th><th className="text-left py-2 pr-4 font-medium text-zinc-500">Type</th><th className="text-left py-2 font-medium text-zinc-500">Description</th></tr></thead>
                <tbody className="divide-y divide-zinc-100 ">
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">target_ticket_id</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">integer</td><td className="py-2 text-xs text-zinc-600 ">Ticket number to merge into (required)</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">merged_by_email</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">Email of the member performing the merge (required)</td></tr>
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s -X POST "https://api.usekora.app/public/v1/tickets/42/merge" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456" \\\n  -H "Content-Type: application/json" \\\n  -d '{"target_ticket_id": 45, "merged_by_email": "admin@acme.com"}'` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets/42/merge", {\n  method: "POST",\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json" },\n  body: JSON.stringify({ target_ticket_id: 45, merged_by_email: "admin@acme.com" })\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.post(\n    "https://api.usekora.app/public/v1/tickets/42/merge",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json"},\n    json={"target_ticket_id": 45, "merged_by_email": "admin@acme.com"}\n)` },
              { label: 'Go', language: 'go', code: `body := map[string]interface{}{"target_ticket_id": 45, "merged_by_email": "admin@acme.com"}\njsonData, _ := json.Marshal(body)\nreq, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/tickets/42/merge", bytes.NewBuffer(jsonData))\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "source_ticket": { "ticket_number": 42, "title": "Login crash", "status": "Closed" },
  "target_ticket": { "ticket_number": 45, "title": "Auth issues", "status": "Open" },
  "merged_by": "Admin User"
}`}</CodeBlock>
          </EndpointCard>

          {/* Bulk Update */}
          <EndpointCard
            id="bulk-update"
            method="POST"
            path="/tickets/bulk"
            description="Update status and/or assignee for multiple tickets at once."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Update up to 100 tickets in a single request. All ticket IDs must belong to the authenticated
              workspace. Returns the count of updated tickets and any IDs that were not found.
            </p>
            <SectionTitle>Request Body</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 "><th className="text-left py-2 pr-4 font-medium text-zinc-500">Field</th><th className="text-left py-2 pr-4 font-medium text-zinc-500">Type</th><th className="text-left py-2 font-medium text-zinc-500">Description</th></tr></thead>
                <tbody className="divide-y divide-zinc-100 ">
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">ticket_ids</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">integer[]</td><td className="py-2 text-xs text-zinc-600 ">Array of ticket numbers (required, max 100)</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">status</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">New status (optional)</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">assignee_email</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">Email of the new assignee (optional)</td></tr>
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s -X POST "https://api.usekora.app/public/v1/tickets/bulk" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456" \\\n  -H "Content-Type: application/json" \\\n  -d '{"ticket_ids": [1, 2, 3], "status": "Closed"}'` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets/bulk", {\n  method: "POST",\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json" },\n  body: JSON.stringify({ ticket_ids: [1, 2, 3], status: "Closed" })\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.post(\n    "https://api.usekora.app/public/v1/tickets/bulk",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json"},\n    json={"ticket_ids": [1, 2, 3], "status": "Closed"}\n)` },
              { label: 'Go', language: 'go', code: `body := map[string]interface{}{"ticket_ids": []int{1, 2, 3}, "status": "Closed"}\njsonData, _ := json.Marshal(body)\nreq, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/tickets/bulk", bytes.NewBuffer(jsonData))\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "updated_count": 3,
  "not_found": []
}`}</CodeBlock>
          </EndpointCard>

          {/* ── COMMENTS ── */}

          {/* Get Comments */}
          <EndpointCard
            id="get-comments"
            method="GET"
            path="/tickets/:ticket_id/comments"
            description="Fetch comments and attachments for a ticket."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns all comments (and their attachments) for a specific ticket, ordered
              oldest-first. The <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">:ticket_id</code> is
              the ticket's numeric <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">ticket_number</code>.
            </p>
            <SectionTitle id="get-comments-path-parameters">Path Parameters</SectionTitle>
            <ParamTable
              headers={['Parameter', 'Type', 'Required', 'Description']}
              rows={[
                [{ mono: true, text: 'ticket_id' }, 'integer', { req: true, text: '' }, "The ticket's numeric identifier (ticket_number), e.g. 42."],
              ]}
            />
            <SectionTitle id="get-comments-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl https://api.usekora.app/public/v1/tickets/42/comments \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456"` },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets/42/comments", {
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456"
  }
});
const data = await res.json();` },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.get(
    "https://api.usekora.app/public/v1/tickets/42/comments",
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/tickets/42/comments", nil)
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
body, _ := io.ReadAll(res.Body)
fmt.Println(string(body))` },
            ]} />
            <SectionTitle id="get-comments-response">Response</SectionTitle>
            <CodeBlock language="json">{`{
              "success": true,
              "workspace_id": "ws_abc123",
              "ticket_id": 42,
              "comments": [
              {
                "id": 1,
              "content": "Reproduced locally. Digging into the auth middleware.",
              "parent_id": null,
              "created_at": "2026-06-15T10:05:00Z",
              "user": "Jane Smith",
              "attachments": [
              {
                "id": 1,
              "file_name": "screenshot.png",
              "file_type": "image/png",
              "file_size": 204800,
              "storage_url": "https://..."
        }
              ]
    }
              ],
              "total_count": 1
}`}</CodeBlock>
          </EndpointCard>

          {/* Add Comment */}
          <EndpointCard
            id="add-comment"
            method="POST"
            path="/tickets/:ticket_id/comments"
            description="Add a comment to a ticket."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Creates a new comment on the specified ticket. The{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">author_email</code>{' '}
              must be an active workspace member. Supports threaded replies via{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">parent_id</code>.
            </p>
            <SectionTitle id="add-comment-request-body">Request Body</SectionTitle>
            <ParamTable
              headers={['Field', 'Type', 'Required', 'Description']}
              rows={[
                [{ mono: true, text: 'content' }, 'string', { req: true, text: '' }, 'Comment body text.'],
                [{ mono: true, text: 'author_email' }, 'string', { req: true, text: '' }, 'Email of an active workspace member who is the comment author.'],
                [{ mono: true, text: 'parent_id' }, 'integer', { req: false, text: '' }, 'ID of the parent comment for threaded replies. null for top-level comments.'],
              ]}
            />
            <SectionTitle id="add-comment-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl -X POST https://api.usekora.app/public/v1/tickets/42/comments \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456" \\
  -H "Content-Type: application/json" \\
  -d '{
    "content": "Fixed in PR #123. Awaiting review.",
    "author_email": "jane@example.com"
  }'` },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets/42/comments", {
  method: "POST",
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    content: "Fixed in PR #123. Awaiting review.",
    author_email: "jane@example.com"
  })
});
const data = await res.json();` },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.post(
    "https://api.usekora.app/public/v1/tickets/42/comments",
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456",
        "Content-Type": "application/json"
    },
    json={
        "content": "Fixed in PR #123. Awaiting review.",
        "author_email": "jane@example.com"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `body := map[string]interface{}{
    "content": "Fixed in PR #123. Awaiting review.",
    "author_email": "jane@example.com",
}
jsonData, _ := json.Marshal(body)
req, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/tickets/42/comments", bytes.NewBuffer(jsonData))
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
req.Header.Set("Content-Type", "application/json")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
resBody, _ := io.ReadAll(res.Body)
fmt.Println(string(resBody))` },
            ]} />
            <SectionTitle id="add-comment-response">Response <span className="text-zinc-400 font-normal text-xs ml-1">201 Created</span></SectionTitle>
            <CodeBlock language="json">{`{
              "success": true,
              "workspace_id": "ws_abc123",
              "ticket_id": 42,
              "comment": {
                "id": 2,
              "content": "Fixed in PR #123. Awaiting review.",
              "user": "Jane Smith",
              "parent_id": null,
              "attachments": [],
              "created_at": "2026-06-15T10:30:00Z"
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* Comments Since */}
          <EndpointCard
            id="comments-since"
            method="GET"
            path="/tickets/:ticket_id/comments/since"
            description="Get comments created after a given timestamp — ideal for realtime polling."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns all comments on a ticket created after the{' '}
              <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">since</code> timestamp.
              Use this for incremental polling — call every 5\u201310 seconds to get new comments without refetching the entire thread.
            </p>
            <SectionTitle>Query Parameters</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 "><th className="text-left py-2 pr-4 font-medium text-zinc-500">Parameter</th><th className="text-left py-2 pr-4 font-medium text-zinc-500">Type</th><th className="text-left py-2 font-medium text-zinc-500">Description</th></tr></thead>
                <tbody className="divide-y divide-zinc-100 ">
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">since</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">datetime</td><td className="py-2 text-xs text-zinc-600 ">ISO 8601 timestamp (required, e.g. 2026-01-01T00:00:00Z)</td></tr>
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s "https://api.usekora.app/public/v1/tickets/42/comments/since?since=2026-08-22T00:00:00Z" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456"` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets/42/comments/since?since=2026-08-22T00:00:00Z", {\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456" }\n});\nconst data = await res.json();` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.get(\n    "https://api.usekora.app/public/v1/tickets/42/comments/since",\n    params={"since": "2026-08-22T00:00:00Z"},\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456"}\n)` },
              { label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/tickets/42/comments/since?since=2026-08-22T00:00:00Z", nil)\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")\nres, _ := http.DefaultClient.Do(req)` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "ticket_id": 42,
  "since": "2026-08-22T00:00:00Z",
  "comments": [
    { "id": 15, "content": "Updated the auth flow", "user": "Jane Smith", "created_at": "2026-08-22T10:30:00Z" }
  ],
  "total_count": 1
}`}</CodeBlock>
          </EndpointCard>

          {/* ── ATTACHMENTS ── */}

          {/* List Attachments */}
          <EndpointCard
            id="list-attachments"
            method="GET"
            path="/tickets/:ticket_id/attachments"
            description="List all file attachments for a ticket."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns all attachments uploaded to a specific ticket, sorted by upload time (newest first).
            </p>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s "https://api.usekora.app/public/v1/tickets/42/attachments" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456"` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/tickets/42/attachments", {\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456" }\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.get(\n    "https://api.usekora.app/public/v1/tickets/42/attachments",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456"}\n)` },
              { label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/tickets/42/attachments", nil)\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "ticket_id": 42,
  "attachments": [
    {
      "id": 8,
      "file_name": "screenshot.png",
      "file_type": "image/png",
      "file_size": 245678,
      "storage_url": "https://files.usekora.app/attachments/123_screenshot.png",
      "uploaded_at": "2026-08-22 10:30:00"
    }
  ],
  "total_count": 1
}`}</CodeBlock>
          </EndpointCard>

          {/* Upload Attachment */}
          <EndpointCard
            id="upload-attachment"
            method="POST"
            path="/tickets/:ticket_id/attachments"
            description="Upload a file attachment to a ticket."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Uploads a file to Cloudflare R2 storage and links it to the ticket. Request must be{' '}
              <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">multipart/form-data</code>.
              File size is validated against the workspace plan limit.
            </p>
            <SectionTitle>Form Fields</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 "><th className="text-left py-2 pr-4 font-medium text-zinc-500">Field</th><th className="text-left py-2 pr-4 font-medium text-zinc-500">Type</th><th className="text-left py-2 font-medium text-zinc-500">Description</th></tr></thead>
                <tbody className="divide-y divide-zinc-100 ">
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">file</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">binary</td><td className="py-2 text-xs text-zinc-600 ">The file to upload (required)</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">uploader_email</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">Email of the member uploading the file (required)</td></tr>
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s -X POST "https://api.usekora.app/public/v1/tickets/42/attachments" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456" \\\n  -F "file=@screenshot.png" \\\n  -F "uploader_email=agent@acme.com"` },
              { label: 'JavaScript', language: 'javascript', code: `const formData = new FormData();\nformData.append("file", fileInput.files[0]);\nformData.append("uploader_email", "agent@acme.com");\n\nconst res = await fetch("https://api.usekora.app/public/v1/tickets/42/attachments", {\n  method: "POST",\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456" },\n  body: formData\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nwith open("screenshot.png", "rb") as f:\n    res = requests.post(\n        "https://api.usekora.app/public/v1/tickets/42/attachments",\n        headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456"},\n        files={"file": f},\n        data={"uploader_email": "agent@acme.com"}\n    )` },
              { label: 'Go', language: 'go', code: `var buf bytes.Buffer\nw := multipart.NewWriter(&buf)\nfw, _ := w.CreateFormFile("file", "screenshot.png")\nfile, _ := os.Open("screenshot.png")\nio.Copy(fw, file)\nw.WriteField("uploader_email", "agent@acme.com")\nw.Close()\nreq, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/tickets/42/attachments", &buf)\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")\nreq.Header.Set("Content-Type", w.FormDataContentType())` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">201 Created</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "ticket_id": 42,
  "attachment": {
    "id": 9,
    "file_name": "screenshot.png",
    "file_type": "image/png",
    "file_size": 245678,
    "storage_url": "https://files.usekora.app/attachments/123_screenshot.png",
    "uploaded_by": "Jane Smith"
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* ── ANALYTICS ── */}

          {/* Dashboard Metrics */}
          <EndpointCard
            id="dashboard-metrics"
            method="GET"
            path="/dashboard/metrics"
            description="Monthly analytics for custom dashboards and reports."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns aggregated metrics and chart data for a given calendar month. Useful for
              building custom dashboards, weekly reports, or Slack digests.
            </p>
            <SectionTitle id="dashboard-metrics-query-parameters">Query Parameters</SectionTitle>
            <ParamTable
              headers={['Parameter', 'Type', 'Required', 'Description']}
              rows={[
                [{ mono: true, text: 'month' }, 'string', { req: true, text: '' }, 'Calendar month to query. Format: YYYY-MM, e.g. 2026-06.'],
              ]}
            />
            <SectionTitle id="dashboard-metrics-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl "https://api.usekora.app/public/v1/dashboard/metrics?month=2026-06" \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456"` },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/dashboard/metrics?month=2026-06", {
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456"
  }
});
const data = await res.json();` },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.get(
    "https://api.usekora.app/public/v1/dashboard/metrics",
    params={"month": "2026-06"},
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/dashboard/metrics?month=2026-06", nil)
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
body, _ := io.ReadAll(res.Body)
fmt.Println(string(body))` },
            ]} />
            <SectionTitle id="dashboard-metrics-response">Response</SectionTitle>
            <CodeBlock language="json">{`{
              "success": true,
              "workspace_id": "ws_abc123",
              "month": "2026-06",
              "metrics": {
                "tickets_created": 134,
              "tickets_open": 22,
              "tickets_closed": 112,
              "tickets_reopened": 5
  },
              "charts": {
                "status_distribution": {
                "Open": 18,
              "In Progress": 4,
              "Resolved": 107,
              "Closed": 5
    },
              "tickets_by_user": [
              {"user": "Alice", "count": 41 },
              {"user": "Bob", "count": 29 }
              ],
              "tickets_by_category": [
              {"category": "Bug", "count": 58 },
              {"category": "Feature Request", "count": 46 }
              ],
              "daily_trend": [
              {"date": "2026-06-01", "count": 6 },
              {"date": "2026-06-02", "count": 9 }
              ]
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* List Members */}
          <EndpointCard
            id="list-members"
            method="GET"
            path="/members"
            description="List all active workspace members."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns a list of all active members in your workspace, ordered by join date.
              Only members with <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">active</code> status are included.
            </p>
            <SectionTitle id="list-members-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl https://api.usekora.app/public/v1/members \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456"`,
              },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/members", {
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456"
  }
});
const data = await res.json();`,
              },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.get(
    "https://api.usekora.app/public/v1/members",
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456"
    }
)
print(res.json())`,
              },
              {
                label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/members", nil)
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
body, _ := io.ReadAll(res.Body)
fmt.Println(string(body))`,
              },
            ]} />
            <SectionTitle id="list-members-response">Response</SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "workspace_id": "ws_abc123",
  "members": [
    {
      "id": 1,
      "email": "john@acmesolutions.com",
      "display_name": "John",
      "role": "owner",
      "status": "active",
      "joined_at": "2026-05-01T10:00:00Z",
      "last_active_at": "2026-07-13T18:30:00Z"
    },
    {
      "id": 2,
      "email": "sarah@acmesolutions.com",
      "display_name": "Sarah",
      "role": "admin",
      "status": "active",
      "joined_at": "2026-05-02T09:00:00Z",
      "last_active_at": "2026-07-13T17:15:00Z"
    }
  ],
  "total_count": 2
}`}</CodeBlock>
          </EndpointCard>

          {/* Invite Member */}
          <EndpointCard
            id="invite-member"
            method="POST"
            path="/members"
            description="Invite a new member to the workspace by email."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Creates a workspace membership with status{' '}
              <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">invited</code>.
              The invited user will be linked when they sign up. Each workspace can hold 100 members (active plus pending) and receive 60 invitations per rolling 24 hours; past either limit the call returns 403 or 429 with an error_code of MEMBER_LIMIT_REACHED or DAILY_INVITE_LIMIT.
            </p>
            <SectionTitle>Request Body</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 "><th className="text-left py-2 pr-4 font-medium text-zinc-500">Field</th><th className="text-left py-2 pr-4 font-medium text-zinc-500">Type</th><th className="text-left py-2 font-medium text-zinc-500">Description</th></tr></thead>
                <tbody className="divide-y divide-zinc-100 ">
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">email</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">Email address to invite (required)</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">role</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">Role: manager, operations, agent, viewer, admin (required)</td></tr>
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s -X POST "https://api.usekora.app/public/v1/members" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456" \\\n  -H "Content-Type: application/json" \\\n  -d '{"email": "newagent@acme.com", "role": "agent"}'` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/members", {\n  method: "POST",\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json" },\n  body: JSON.stringify({ email: "newagent@acme.com", role: "agent" })\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.post(\n    "https://api.usekora.app/public/v1/members",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json"},\n    json={"email": "newagent@acme.com", "role": "agent"}\n)` },
              { label: 'Go', language: 'go', code: `body := map[string]string{"email": "newagent@acme.com", "role": "agent"}\njsonData, _ := json.Marshal(body)\nreq, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/members", bytes.NewBuffer(jsonData))\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">201 Created</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "invitation": {
    "email": "newagent@acme.com",
    "role": "agent",
    "status": "invited",
    "invited_at": "2026-08-22T10:30:00.000Z"
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* Update Member Role */}
          <EndpointCard
            id="update-member"
            method="PATCH"
            path="/members/:member_id"
            description="Update a workspace member's role."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Changes the role of an active workspace member. The <code className="text-xs">member_id</code> is the
              user's ID returned by the List Members endpoint. Owner roles cannot be changed or assigned via this API.
            </p>
            <SectionTitle>Request Body</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 "><th className="text-left py-2 pr-4 font-medium text-zinc-500">Field</th><th className="text-left py-2 pr-4 font-medium text-zinc-500">Type</th><th className="text-left py-2 font-medium text-zinc-500">Description</th></tr></thead>
                <tbody className="divide-y divide-zinc-100 ">
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">role</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">New role: admin, manager, operations, agent, viewer (required)</td></tr>
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s -X PATCH "https://api.usekora.app/public/v1/members/480" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456" \\\n  -H "Content-Type: application/json" \\\n  -d '{"role": "manager"}'` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/members/480", {\n  method: "PATCH",\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json" },\n  body: JSON.stringify({ role: "manager" })\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.patch(\n    "https://api.usekora.app/public/v1/members/480",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json"},\n    json={"role": "manager"}\n)` },
              { label: 'Go', language: 'go', code: `body := map[string]string{"role": "manager"}\njsonData, _ := json.Marshal(body)\nreq, _ := http.NewRequest("PATCH", "https://api.usekora.app/public/v1/members/480", bytes.NewBuffer(jsonData))\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "member": {
    "id": 480,
    "email": "agent@acme.com",
    "name": "Jane Smith",
    "old_role": "agent",
    "new_role": "manager",
    "updated_at": "2026-08-22T10:30:00.000Z"
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* ── WEBHOOKS API ── */}

          {/* Create Webhook via API */}
          <EndpointCard
            id="create-webhook-api"
            method="POST"
            path="/webhooks"
            description="Create a new webhook endpoint programmatically."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Creates a new webhook endpoint. The URL must use HTTPS. Up to 10 webhooks per workspace.
              If <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">events</code> is omitted, all events are subscribed by default.
            </p>
            <SectionTitle id="create-webhook-api-request-body">Request Body</SectionTitle>
            <ParamTable
              headers={['Field', 'Type', 'Required', 'Description']}
              rows={[
                [{ mono: true, text: 'url' }, 'string', { req: true, text: '' }, 'HTTPS URL where webhook deliveries will be sent.'],
                [{ mono: true, text: 'description' }, 'string', { req: false, text: '' }, 'Optional description for the webhook.'],
                [{ mono: true, text: 'events' }, 'string[]', { req: false, text: '' }, 'Array of event names to subscribe to. Defaults to all events.'],
                [{ mono: true, text: 'secret_token' }, 'string', { req: false, text: '' }, 'Optional secret token for HMAC-SHA256 signature verification.'],
              ]}
            />
            <SectionTitle id="create-webhook-api-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl -X POST https://api.usekora.app/public/v1/webhooks \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://your-app.com/webhooks/kora",
    "description": "Production webhook",
    "events": ["ticket.created", "ticket.resolved", "ticket.closed"]
  }'`,
              },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/webhooks", {
  method: "POST",
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    url: "https://your-app.com/webhooks/kora",
    description: "Production webhook",
    events: ["ticket.created", "ticket.resolved", "ticket.closed"]
  })
});
const data = await res.json();`,
              },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.post(
    "https://api.usekora.app/public/v1/webhooks",
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456",
        "Content-Type": "application/json"
    },
    json={
        "url": "https://your-app.com/webhooks/kora",
        "description": "Production webhook",
        "events": ["ticket.created", "ticket.resolved", "ticket.closed"]
    }
)
print(res.json())`,
              },
              {
                label: 'Go', language: 'go', code: `body := map[string]interface{}{
    "url": "https://your-app.com/webhooks/kora",
    "description": "Production webhook",
    "events": []string{"ticket.created", "ticket.resolved", "ticket.closed"},
}
jsonData, _ := json.Marshal(body)
req, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/webhooks", bytes.NewBuffer(jsonData))
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
req.Header.Set("Content-Type", "application/json")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
resBody, _ := io.ReadAll(res.Body)
fmt.Println(string(resBody))`,
              },
            ]} />
            <SectionTitle id="create-webhook-api-response">Response <span className="text-zinc-400 font-normal text-xs ml-1">201 Created</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "workspace_id": "ws_abc123",
  "webhook": {
    "id": 3,
    "url": "https://your-app.com/webhooks/kora",
    "description": "Production webhook",
    "events": ["ticket.created", "ticket.resolved", "ticket.closed"],
    "status": "active",
    "has_secret": false
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* List Webhooks */}
          <EndpointCard
            id="list-webhooks"
            method="GET"
            path="/webhooks"
            description="List all webhooks for the authenticated workspace."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns all webhooks configured for the workspace, sorted by creation date (newest first).
              Secret tokens are never returned — only a boolean <code className="text-xs">has_secret</code> flag.
            </p>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s "https://api.usekora.app/public/v1/webhooks" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456"` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/webhooks", {\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456" }\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.get(\n    "https://api.usekora.app/public/v1/webhooks",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456"}\n)` },
              { label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/webhooks", nil)\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "webhooks": [
    {
      "id": 3,
      "url": "https://your-app.com/webhooks/kora",
      "description": "Production webhook",
      "events": ["ticket.created", "ticket.resolved"],
      "is_active": true,
      "has_secret": false,
      "created_at": "2026-08-01 10:00:00"
    }
  ],
  "total_count": 1,
  "max_allowed": 10
}`}</CodeBlock>
          </EndpointCard>

          {/* Update Webhook */}
          <EndpointCard
            id="update-webhook"
            method="PATCH"
            path="/webhooks/:webhook_id"
            description="Update a webhook's URL, events, active state, or secret."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              All fields are optional — only provided fields are updated. Pass{' '}
              <code className="text-xs">events: []</code> to subscribe to all events.
            </p>
            <SectionTitle>Request Body (all optional)</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 "><th className="text-left py-2 pr-4 font-medium text-zinc-500">Field</th><th className="text-left py-2 pr-4 font-medium text-zinc-500">Type</th><th className="text-left py-2 font-medium text-zinc-500">Description</th></tr></thead>
                <tbody className="divide-y divide-zinc-100 ">
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">url</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">New webhook URL (must be HTTPS)</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">description</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">Description (max 200 chars)</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">events</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string[]</td><td className="py-2 text-xs text-zinc-600 ">Array of event names, or empty array for all</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">is_active</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">boolean</td><td className="py-2 text-xs text-zinc-600 ">Enable or disable the webhook</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">secret_token</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">string</td><td className="py-2 text-xs text-zinc-600 ">New secret token for signature verification</td></tr>
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s -X PATCH "https://api.usekora.app/public/v1/webhooks/3" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456" \\\n  -H "Content-Type: application/json" \\\n  -d '{"description": "Updated description", "is_active": false}'` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/webhooks/3", {\n  method: "PATCH",\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json" },\n  body: JSON.stringify({ description: "Updated", is_active: false })\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.patch(\n    "https://api.usekora.app/public/v1/webhooks/3",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json"},\n    json={"description": "Updated", "is_active": False}\n)` },
              { label: 'Go', language: 'go', code: `body := map[string]interface{}{"description": "Updated", "is_active": false}\njsonData, _ := json.Marshal(body)\nreq, _ := http.NewRequest("PATCH", "https://api.usekora.app/public/v1/webhooks/3", bytes.NewBuffer(jsonData))\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{ "success": true }`}</CodeBlock>
          </EndpointCard>

          {/* Delete Webhook */}
          <EndpointCard
            id="delete-webhook"
            method="DELETE"
            path="/webhooks/:webhook_id"
            description="Delete a webhook and all its delivery history."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Permanently deletes the webhook and all associated delivery records. This operation is irreversible.
            </p>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s -X DELETE "https://api.usekora.app/public/v1/webhooks/3" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456"` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/webhooks/3", {\n  method: "DELETE",\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456" }\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.delete(\n    "https://api.usekora.app/public/v1/webhooks/3",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456"}\n)` },
              { label: 'Go', language: 'go', code: `req, _ := http.NewRequest("DELETE", "https://api.usekora.app/public/v1/webhooks/3", nil)\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{ "success": true, "deleted": true }`}</CodeBlock>
          </EndpointCard>

          {/* Test Webhook */}
          <EndpointCard
            id="test-webhook"
            method="POST"
            path="/webhooks/:webhook_id/test"
            description="Send a test payload to a webhook to verify delivery."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Triggers a test delivery to the webhook URL. Useful for verifying that the endpoint is reachable
              and correctly receiving Kora payloads.
            </p>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s -X POST "https://api.usekora.app/public/v1/webhooks/3/test" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456" \\\n  -H "Content-Type: application/json" \\\n  -d '{}'` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/webhooks/3/test", {\n  method: "POST",\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json" },\n  body: "{}"\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.post(\n    "https://api.usekora.app/public/v1/webhooks/3/test",\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456", "Content-Type": "application/json"},\n    json={}\n)` },
              { label: 'Go', language: 'go', code: `req, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/webhooks/3/test", bytes.NewBufferString("{}"))\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "webhook_id": 3,
  "delivery": { "status": "delivered" }
}`}</CodeBlock>
          </EndpointCard>

          {/* Webhook Deliveries */}
          <EndpointCard
            id="webhook-deliveries"
            method="GET"
            path="/webhooks/:webhook_id/deliveries"
            description="Get paginated delivery history for a webhook."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Returns delivery records sorted by creation date (newest first). Use{' '}
              <code className="text-xs">page</code> and <code className="text-xs">limit</code> query parameters for pagination.
            </p>
            <SectionTitle>Query Parameters</SectionTitle>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-zinc-200 "><th className="text-left py-2 pr-4 font-medium text-zinc-500">Parameter</th><th className="text-left py-2 pr-4 font-medium text-zinc-500">Type</th><th className="text-left py-2 font-medium text-zinc-500">Description</th></tr></thead>
                <tbody className="divide-y divide-zinc-100 ">
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">page</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">integer</td><td className="py-2 text-xs text-zinc-600 ">Page number (default: 1)</td></tr>
                  <tr><td className="py-2 pr-4 font-mono text-xs text-blue-600 ">limit</td><td className="py-2 pr-4 font-mono text-xs text-zinc-500">integer</td><td className="py-2 text-xs text-zinc-600 ">Items per page (default: 20, max: 50)</td></tr>
                </tbody>
              </table>
            </div>
            <CodeBlockTabs snippets={[
              { label: 'cURL', language: 'bash', code: `curl -s "https://api.usekora.app/public/v1/webhooks/3/deliveries?page=1&limit=20" \\\n  -H "X-Workspace-ID: ws_abc123" \\\n  -H "X-Secret-Key: sk_xyz456"` },
              { label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/webhooks/3/deliveries?page=1&limit=20", {\n  headers: { "X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456" }\n});` },
              { label: 'Python', language: 'python', code: `import requests\n\nres = requests.get(\n    "https://api.usekora.app/public/v1/webhooks/3/deliveries",\n    params={"page": 1, "limit": 20},\n    headers={"X-Workspace-ID": "ws_abc123", "X-Secret-Key": "sk_xyz456"}\n)` },
              { label: 'Go', language: 'go', code: `req, _ := http.NewRequest("GET", "https://api.usekora.app/public/v1/webhooks/3/deliveries?page=1&limit=20", nil)\nreq.Header.Set("X-Workspace-ID", "ws_abc123")\nreq.Header.Set("X-Secret-Key", "sk_xyz456")` },
            ]} />
            <SectionTitle>Response <span className="text-zinc-400 font-normal text-xs ml-1">200 OK</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "webhook_id": 3,
  "deliveries": [
    { "id": 15, "event": "ticket.created", "status": "delivered", "response_code": 200, "created_at": "2026-08-22 10:30:00" }
  ],
  "pagination": { "total": 1, "page": 1, "limit": 20, "total_pages": 1 }
}`}</CodeBlock>
          </EndpointCard>

          {/* ── EMBEDDED SESSIONS ── */}

          {/* Embedded Sessions Overview */}
          <DocCard id="embedded-overview" title="Embedded Sessions">
            <p className="text-zinc-600 text-sm leading-relaxed mb-4">
              Embedded Sessions allow you to embed the Kora tickets board directly into your product via iframe — no
              separate login required for your users. Call the API to generate a one-time session URL,
              embed it, and Kora handles the rest. The embedded view renders the full tickets board
              with role-based access, search, filters, and ticket management.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {[
                { label: 'Availability', value: 'Every workspace' },
                { label: 'Auth', value: 'Workspace ID + Secret Key' },
                { label: 'Token type', value: 'Time-limited' },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-zinc-200 p-4 bg-zinc-50/50 "
                >
                  <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400 mb-1">
                    {item.label}
                  </p>
                  <p className="text-sm font-semibold text-zinc-900 ">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 flex gap-3">
              <Info className="size-5 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800 ">
                <strong>How it works:</strong> 1) Call <code className="text-xs bg-blue-100 px-1 rounded">POST /public/v1/sessions</code> with your workspace credentials.
                2) API returns a one-time session URL. 3) Embed the URL in an iframe. 4) Kora auto-authenticates the user and renders the tickets board with role-based permissions.
              </p>
            </div>
          </DocCard>

          {/* Create Session Endpoint */}
          <EndpointCard
            id="create-session"
            method="POST"
            path="/public/v1/sessions"
            description="Create an embedded session for external application embedding."
          >
            <p className="text-zinc-600 text-sm leading-relaxed mb-6">
              Creates a session URL that auto-authenticates the specified user and renders
              Kora inside an iframe. The session token is valid for the configured duration and can be refreshed until expiry.
            </p>
            <SectionTitle id="create-session-request-body">Request Body</SectionTitle>
            <ParamTable
              headers={['Field', 'Type', 'Required', 'Description']}
              rows={[
                [{ mono: true, text: 'user_email' }, 'string', { req: true, text: '' }, 'Email of the user this session is for. Must be an active workspace member.'],
                [{ mono: true, text: 'duration_minutes' }, 'integer', { req: true, text: '' }, 'Session duration in minutes. Min: 5, Max: 1440 (24 hours). Default: 480.'],
                [{ mono: true, text: 'theme' }, 'string', { req: false, text: '' }, 'Theme for embedded session. Default: light. Options: light, dark.'],
              ]}
            />
            <SectionTitle id="create-session-request">Request</SectionTitle>
            <CodeBlockTabs snippets={[
              {
                label: 'curl', language: 'bash', code: `curl -X POST https://api.usekora.app/public/v1/sessions \\
  -H "X-Workspace-ID: ws_abc123" \\
  -H "X-Secret-Key: sk_xyz456" \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_email": "john@example.com",
    "duration_minutes": 480,
    "theme": "light"
  }'` },
              {
                label: 'JavaScript', language: 'javascript', code: `const res = await fetch("https://api.usekora.app/public/v1/sessions", {
  method: "POST",
  headers: {
    "X-Workspace-ID": "ws_abc123",
    "X-Secret-Key": "sk_xyz456",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    user_email: "john@example.com",
    duration_minutes: 480,
    theme: "light"
  })
});
const data = await res.json();` },
              {
                label: 'Python', language: 'python', code: `import requests

res = requests.post(
    "https://api.usekora.app/public/v1/sessions",
    headers={
        "X-Workspace-ID": "ws_abc123",
        "X-Secret-Key": "sk_xyz456",
        "Content-Type": "application/json"
    },
    json={
        "user_email": "john@example.com",
        "duration_minutes": 480,
        "theme": "light"
    }
)
print(res.json())` },
              {
                label: 'Go', language: 'go', code: `body := map[string]interface{}{
    "user_email": "john@example.com",
    "duration_minutes": 480,
    "theme": "light",
}
jsonData, _ := json.Marshal(body)
req, _ := http.NewRequest("POST", "https://api.usekora.app/public/v1/sessions", bytes.NewBuffer(jsonData))
req.Header.Set("X-Workspace-ID", "ws_abc123")
req.Header.Set("X-Secret-Key", "sk_xyz456")
req.Header.Set("Content-Type", "application/json")
res, _ := http.DefaultClient.Do(req)
defer res.Body.Close()
resBody, _ := io.ReadAll(res.Body)
fmt.Println(string(resBody))` },
            ]} />
            <SectionTitle id="create-session-response">Response <span className="text-zinc-400 font-normal text-xs ml-1">201 Created</span></SectionTitle>
            <CodeBlock language="json">{`{
  "success": true,
  "session": {
    "session_id": "sess_abc123xyz",
    "session_url": "https://usekora.app/embedd/sess_abc123xyz",
    "embed_code": "<iframe src='https://usekora.app/embedd/sess_abc123xyz' width='100%' height='100%' frameborder='0' style='border:none;'></iframe>",
    "expires_at": "2026-06-16T23:39:00Z",
    "duration_minutes": 480
  }
}`}</CodeBlock>
          </EndpointCard>

          {/* Embedding Examples */}
          <DocCard id="embed-examples" title="Embedding Examples">
            <SectionTitle id="embed-examples-html">HTML</SectionTitle>
            <CodeBlock language="html">{`<iframe
  src="https://usekora.app/embedd/sess_abc123xyz"
  width="100%"
  height="100vh"
  frameborder="0"
  style="border: none;">
</iframe>`}</CodeBlock>

            <SectionTitle id="embed-examples-react">React</SectionTitle>
            <CodeBlock language="jsx">{`function KoraEmbed({ sessionUrl }) {
  return (
    <iframe
      src={sessionUrl}
      width="100%"
      height="100vh"
      frameBorder="0"
      style={{ border: 'none' }}
    />
  );
}`}</CodeBlock>

            <SectionTitle id="embed-examples-vue">Vue</SectionTitle>
            <CodeBlock language="html">{`<template>
  <iframe
    :src="sessionUrl"
    width="100%"
    height="100vh"
    frameborder="0"
    style="border: none;"
  />
</template>

<script setup>
defineProps({ sessionUrl: String });
</script>`}</CodeBlock>

            <SectionTitle id="embed-examples-python-djangoflask">Python (Django/Flask)</SectionTitle>
            <CodeBlock language="html">{`<iframe
  src="{{ kora_session_url }}"
  width="100%"
  height="800px"
  frameborder="0">
</iframe>`}</CodeBlock>

            <SectionTitle id="embed-examples-nextjs-serverside-session-crea">Next.js (Server-side session creation)</SectionTitle>
            <CodeBlock language="javascript">{`// app/api/kora-session/route.ts
import { NextResponse } from 'next/server';

export async function POST(req: Request) {
  const { user_email } = await req.json();

  const res = await fetch('https://api.usekora.app/public/v1/sessions', {
    method: 'POST',
    headers: {
      'X-Workspace-ID': process.env.KORA_WORKSPACE_ID!,
      'X-Secret-Key': process.env.KORA_SECRET_KEY!,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      user_email,
      duration_minutes: 480,
      theme: 'light',
    }),
  });

  const data = await res.json();
  return NextResponse.json(data);
}`}</CodeBlock>
          </DocCard>

          {/* Session Behavior */}
          <DocCard id="session-behavior" title="Session Behavior">
            <ParamTable
              headers={['Property', 'Description']}
              rows={[
                [{ mono: true, text: 'Time-limited' }, 'Session stays valid for the configured duration. Refreshing the iframe re-validates successfully until expiry. First access is logged for audit (IP, user agent).'],
                [{ mono: true, text: 'Auto-authentication' }, 'The specified user is automatically logged in — no separate login required.'],
                [{ mono: true, text: 'Auto-expiry' }, 'Session expires after duration_minutes. On expiry, shows "Session Expired" page.'],
                [{ mono: true, text: 'Tickets only' }, 'Embedded sessions render the Kora tickets board exclusively. No navigation to other sections is available within the iframe.'],
                [{ mono: true, text: 'Theme' }, 'Renders in light or dark mode based on the theme parameter.'],
                [{ mono: true, text: 'Role-based access' }, 'The tickets board respects the user role: operations roles see only their own reported tickets, while manager+ roles see all tickets. Ticket creation and claim actions are gated by role permissions.'],
              ]}
            />
          </DocCard>

          {/* Session Security */}
          <DocCard id="session-security" title="Security">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <span className="size-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                <p className="text-sm text-zinc-600 ">
                  Session tokens are cryptographically random and unique.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="size-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                <p className="text-sm text-zinc-600 ">
                  First access is logged (IP, user agent) for audit. Token remains valid until expiry.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="size-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                <p className="text-sm text-zinc-600 ">
                  Sessions are bound to a specific workspace and user.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="size-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                <p className="text-sm text-zinc-600 ">
                  Session usage is logged with IP address and user agent for audit purposes.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span className="size-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                <p className="text-sm text-zinc-600 ">
                  Expired and used sessions are automatically cleaned up.
                </p>
              </div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3 mt-4">
              <AlertTriangle className="size-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 ">
                Always create sessions server-side. Never expose your{' '}
                <code className="text-xs bg-amber-100 px-1 rounded">X-Secret-Key</code>{' '}
                in client-side code.
              </p>
            </div>
          </DocCard>

          {/* ── WEBHOOKS ── */}

          {/* Webhooks Overview */}
          <DocCard id="webhooks-overview" title="Webhooks">
            <p className="text-zinc-600 text-sm leading-relaxed mb-4">
              Webhooks let you receive real-time HTTP notifications when ticket events happen in your
              workspace. When an event occurs, Kora sends a <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">POST</code> request
              to each registered webhook URL with a JSON payload describing the event.
            </p>
            <p className="text-zinc-600 text-sm leading-relaxed mb-4">
              Webhooks are configured from <strong>Settings → Webhooks</strong> in the Kora dashboard.
              You can register up to <strong>10 webhook URLs</strong> per workspace and select which
              events each URL should receive.
            </p>
            <SectionTitle id="webhooks-overview-how-it-works">How it works</SectionTitle>
            <ol className="text-sm text-zinc-600 space-y-2 mb-4 list-decimal list-inside">
              <li>A ticket event occurs (e.g. ticket created, resolved, closed).</li>
              <li>Kora finds all active webhooks subscribed to that event.</li>
              <li>For each matching webhook, Kora sends an HTTP POST with the event payload.</li>
              <li>The delivery is logged with status and response code for debugging.</li>
            </ol>
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex gap-3">
              <Info className="size-5 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800 ">
                Your endpoint must respond with a <code className="text-xs bg-blue-100 px-1 rounded">2xx</code> status code
                within 10 seconds. Failed deliveries are logged but not retried.
              </p>
            </div>
          </DocCard>

          {/* Webhook Event Catalog */}
          <DocCard id="webhook-events" title="Webhook Event Catalog">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              Kora fires the following events. By default, a webhook receives all events. You can
              deselect specific events when creating or editing a webhook.
            </p>
            <ParamTable
              headers={['Event', 'Triggered When', 'Key Data Fields']}
              rows={[
                [{ mono: true, text: 'ticket.created' }, 'A new ticket is created', 'ticket_number, title, priority, status, reporter'],
                [{ mono: true, text: 'ticket.updated' }, 'Ticket fields are edited (title, priority, category, description)', 'ticket_number, changes[], updated_by'],
                [{ mono: true, text: 'ticket.resolved' }, 'Ticket status changes to Resolved', 'ticket_number, previous_status, resolved_by, resolved_at'],
                [{ mono: true, text: 'ticket.closed' }, 'Ticket status changes to Closed', 'ticket_number, previous_status, closed_by, closed_at'],
                [{ mono: true, text: 'ticket.reopened' }, 'Ticket status changes to Reopened', 'ticket_number, previous_status, reopened_by, reopened_at'],
                [{ mono: true, text: 'ticket.assigned' }, 'A ticket is assigned to a member', 'ticket_number, assignee, previous_assignee, assigned_by'],
                [{ mono: true, text: 'ticket.claimed' }, 'An agent claims an unassigned ticket', 'ticket_number, claimed_by, previous_status, claimed_at'],
              ]}
            />
            <SectionTitle id="webhook-events-example-ticketcreated-payload">Example: ticket.created payload</SectionTitle>
            <CodeBlock language="json">{`{
  "event": "ticket.created",
  "workspace_id": "ws_abc123",
  "timestamp": "2026-07-07T13:00:00Z",
  "data": {
    "ticket_number": 43,
    "title": "Checkout button unresponsive on mobile",
    "description": "Tapping the checkout button on iOS Safari does nothing.",
    "category": "Bug",
    "priority": "High",
    "status": "Open",
    "reporter": "John Doe",
    "reporter_email": "john@example.com",
    "assignee": null,
    "assignee_email": null,
    "created_at": "2026-07-07T13:00:00Z"
  }
}`}</CodeBlock>
            <SectionTitle id="webhook-events-example-ticketresolved-payload">Example: ticket.resolved payload</SectionTitle>
            <CodeBlock language="json">{`{
  "event": "ticket.resolved",
  "workspace_id": "ws_abc123",
  "timestamp": "2026-07-07T14:00:00Z",
  "data": {
    "ticket_number": 43,
    "title": "Checkout button unresponsive on mobile",
    "status": "Resolved",
    "previous_status": "In Progress",
    "resolved_by": "Jane Smith",
    "resolved_by_email": "jane@example.com",
    "assignee": "Jane Smith",
    "resolved_at": "2026-07-07T14:00:00Z"
  }
}`}</CodeBlock>
          </DocCard>

          {/* Webhook Payload & Headers */}
          <DocCard id="webhook-payload" title="Payload Structure & Headers">
            <SectionTitle id="webhook-payload-envelope">Envelope</SectionTitle>
            <p className="text-zinc-600 text-sm leading-relaxed mb-4">
              All webhook deliveries share the same envelope structure:
            </p>
            <CodeBlock language="json">{`{
  "event": "ticket.created",
  "workspace_id": "ws_abc123",
  "timestamp": "2026-07-07T13:00:00Z",
  "data": { ... event-specific fields ... }
}`}</CodeBlock>
            <SectionTitle id="webhook-payload-http-headers">HTTP Headers</SectionTitle>
            <ParamTable
              headers={['Header', 'Description']}
              rows={[
                [{ mono: true, text: 'Content-Type' }, 'application/json'],
                [{ mono: true, text: 'X-Kora-Event' }, 'The event name (e.g. ticket.created)'],
                [{ mono: true, text: 'X-Kora-Delivery' }, 'Unique delivery ID for this webhook delivery'],
                [{ mono: true, text: 'X-Kora-Signature' }, 'HMAC-SHA256 signature (only if a secret token is set)'],
              ]}
            />
            <SectionTitle id="webhook-payload-signature-verification">Signature Verification</SectionTitle>
            <p className="text-zinc-600 text-sm leading-relaxed mb-4">
              If you set a secret token when creating a webhook, Kora includes an{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">X-Kora-Signature</code> header
              with the v1 scheme:{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">t=&#123;unix-seconds&#125;,v1=&#123;hex HMAC-SHA256&#125;</code>.
              The signature covers timestamp and raw body together, so you can reject replayed
              deliveries by comparing the timestamp against your own clock (5-minute tolerance is typical).
              The same timestamp is sent in{' '}
              <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded font-mono">X-Kora-Timestamp</code>.
            </p>
            <CodeBlock language="javascript">{`import crypto from 'crypto';

function verifySignature(rawBody, signatureHeader, secret, toleranceSec = 300) {
  const [tPart, v1Part] = signatureHeader.split(',');
  const t = tPart.replace('t=', '');
  const provided = v1Part.replace('v1=', '');
  if (Math.abs(Date.now() / 1000 - Number(t)) > toleranceSec) return false; // replay guard
  const expected = crypto
    .createHmac('sha256', secret)
    .update(t + '.' + rawBody)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected),
    Buffer.from(provided),
  );
}`}</CodeBlock>
          </DocCard>

          {/* Managing Webhooks */}
          <DocCard id="manage-webhooks" title="Managing Webhooks">
            <p className="text-zinc-600 text-sm leading-relaxed mb-4">
              Webhooks are managed from <strong>Settings → Webhooks</strong> in the Kora dashboard.
              Only workspace owners and admins can create, edit, and delete webhooks.
            </p>
            <SectionTitle id="manage-webhooks-from-the-dashboard">From the Dashboard</SectionTitle>
            <ul className="text-sm text-zinc-600 space-y-1.5 mb-4 list-disc list-inside">
              <li>Navigate to Settings → Webhooks</li>
              <li>Enter your HTTPS endpoint URL and optional description</li>
              <li>Select which events the webhook should receive (all selected by default)</li>
              <li>Click "Add Webhook" to register the endpoint</li>
              <li>Use the "Test" button to send a test event and verify your endpoint</li>
              <li>View the delivery log to see recent delivery attempts and their status</li>
              <li>Pause or delete webhooks at any time</li>
            </ul>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
              <AlertTriangle className="size-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 ">
                Webhook URLs must use HTTPS. Up to 10 webhooks per workspace.
              </p>
            </div>
          </DocCard>

          {/* ── REFERENCE ── */}

          {/* Ticket Object */}
          <DocCard id="ticket-object" title="Ticket Object">
            <ParamTable
              headers={['Field', 'Type', 'Description']}
              rows={[
                [{ mono: true, text: 'ticket_number' }, 'integer', 'Auto-incrementing ticket identifier, unique within a workspace.'],
                [{ mono: true, text: 'title' }, 'string', 'Ticket title.'],
                [{ mono: true, text: 'description' }, 'string | null', 'Full description text.'],
                [{ mono: true, text: 'category' }, 'string', 'AI-assigned or user-set category (e.g. Bug, Feature Request).'],
                [{ mono: true, text: 'priority' }, 'string', 'Low | Medium | High | Critical'],
                [{ mono: true, text: 'status' }, 'string', 'Open | Assigned | In Progress | Resolved | Closed | Reopened'],
                [{ mono: true, text: 'reporter' }, 'string', 'Display name or email of the person who created the ticket.'],
                [{ mono: true, text: 'assignee' }, 'string | null', 'Display name or email of the assigned member. null if unassigned.'],
                [{ mono: true, text: 'created_at' }, 'ISO 8601', 'UTC timestamp when the ticket was created.'],
                [{ mono: true, text: 'resolved_at' }, 'ISO 8601 | null', 'UTC timestamp when resolved. null if still open.'],
                [{ mono: true, text: 'updated_at' }, 'ISO 8601', 'UTC timestamp when the ticket was last updated.'],
                [{ mono: true, text: 'attachments' }, 'array', 'List of file attachments. Each has id, file_name, file_type, file_size, storage_url.'],
              ]}
            />
          </DocCard>

          {/* Comment Object */}
          <DocCard id="comment-object" title="Comment Object">
            <ParamTable
              headers={['Field', 'Type', 'Description']}
              rows={[
                [{ mono: true, text: 'id' }, 'integer', 'Unique comment identifier.'],
                [{ mono: true, text: 'content' }, 'string', 'Comment body text.'],
                [{ mono: true, text: 'parent_id' }, 'integer | null', 'ID of the parent comment for threaded replies. null for top-level comments.'],
                [{ mono: true, text: 'user' }, 'string', 'Display name or email of the comment author.'],
                [{ mono: true, text: 'created_at' }, 'ISO 8601', 'UTC timestamp when the comment was posted.'],
                [{ mono: true, text: 'attachments' }, 'array', 'List of file attachments. Each has id, file_name, file_type, file_size (bytes), and storage_url.'],
              ]}
            />
          </DocCard>

          {/* Response Headers */}
          <DocCard id="response-headers" title="Response Headers">
            <p className="text-zinc-600 text-sm leading-relaxed mb-5">
              All successful responses include rate limit headers and CORS headers to support
              browser-based integrations.
            </p>
            <ParamTable
              headers={['Header', 'Description']}
              rows={[
                [{ mono: true, text: 'Content-Type' }, 'application/json'],
                [{ mono: true, text: 'Access-Control-Allow-Origin' }, '* — Public API endpoints are CORS-open.'],
                [{ mono: true, text: 'X-RateLimit-Limit' }, "Your plan's daily request cap."],
                [{ mono: true, text: 'X-RateLimit-Remaining' }, 'Requests you have left today.'],
                [{ mono: true, text: 'X-RateLimit-Reset' }, 'Unix timestamp when the window resets.'],
              ]}
            />
          </DocCard>

          {/* Help */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm flex items-start gap-4">
            <LifeBuoy className="size-6 text-blue-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-zinc-900 mb-1">
                Need help or a custom endpoint?
              </p>
              <p className="text-sm text-zinc-500 ">
                Growth and Custom plan workspaces can request custom API creation. Reach out at{' '}
                <a
                  href="mailto:founder@usekora.app"
                  className="text-blue-500 hover:underline"
                >
                  founder@usekora.app
                </a>{' '}
                — we respond within 48 hours.
              </p>
            </div>
          </div>
        </main>

        {/* TOC */}
        <aside className="hidden xl:block sticky top-20 self-start max-h-[calc(100vh-80px)] overflow-y-auto pl-2">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 px-3 py-2">
            On this page
          </p>
          {(() => {
            // Find which section group the active section belongs to
            const activeGroup = tocSections.find((section) =>
              section.links.some((link) => link.id === activeSection),
            );
            if (!activeGroup) {
              return (
                <p className="text-xs text-zinc-400 px-3 py-2">
                  Scroll to explore
                </p>
              );
            }
            return (
              <nav className="space-y-1">
                {/* Section group label */}
                <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 px-3 mb-2">
                  {activeGroup.title}
                </p>
                {/* Endpoints in the current group */}
                <div className="space-y-0.5">
                  {activeGroup.links.map((link) => {
                    const isActive = activeSection === link.id;
                    const subs = endpointSubsections[link.id];
                    return (
                      <div key={link.id}>
                        <a
                          href={`#${link.id}`}
                          className={cn(
                            'block py-1.5 pl-3 text-xs border-l-2 transition-all',
                            isActive
                              ? 'text-zinc-900 border-[var(--warm-text)] font-semibold'
                              : 'text-zinc-500 border-zinc-200 hover:text-zinc-900 hover:border-blue-400',
                          )}
                        >
                          {link.label}
                        </a>
                        {/* Sub-headings tree — only for the active endpoint */}
                        {isActive && subs && subs.length > 0 && (
                          <div className="ml-3 border-l border-zinc-200 ml-3">
                            {subs.map((sub) => (
                              <a
                                key={sub.id}
                                href={`#${sub.id}`}
                                className={cn(
                                  'block py-1 pl-3 text-[11px] border-l-2 transition-all',
                                  activeSubsection === sub.id
                                    ? 'text-zinc-900 border-[var(--warm-text)] font-medium'
                                    : 'text-zinc-400 border-transparent hover:text-zinc-700 ',
                                )}
                              >
                                {sub.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </nav>
            );
          })()}
        </aside>
      </div>

      <MarketingFooter />
    </div>
  );
}
